<template>
  <div class="transcoding-output-config ">
    <!-- Header with Type Selection -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="bg-[#ffaa00]/15 p-2 rounded-sm ">
           <i class="bi bi-broadcast text-[#ffaa00] text-base"></i>
           <ma-broadcast-custom class="text-[#ffaa00] text-base"></ma-broadcast-custom>
        </div>
        <div>
          <h4 class="text-xs font-black uppercase tracking-widest text-[#e5eefc]">MediaLive Output Configuration</h4>
          <p class="text-[10px] text-[#cdd6ee] font-medium">Group and Individual Output Settings</p>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <span class="text-[10px] text-[#cdd6ee] font-black uppercase tracking-widest">Protocol:</span>
        <select
          v-model="config.outputGroup.type"
          class="bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-bold focus:outline-none focus:ring-2 focus:ring-[#ffaa00]/40 transition-all cursor-pointer"
          @change="updateJson"
        >
          <option value="HLS">HLS</option>
          <option value="ARCHIVE">ARCHIVE</option>
          <option value="RTMP">RTMP</option>
        </select>
      </div>
    </div>

    <div class="space-y-8">
      <!-- Section: Output Group Settings -->
      <section class="space-y-6">
        <div class="flex items-center gap-3 mt-4">
          <span class="text-[10px] font-black text-[#cdd6ee] uppercase tracking-widest">Output Group Settings</span>
        </div>

        <div class="grid grid-cols-2 gap-6 ">
          <div class="space-y-1.5">
            <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Group Name</label>
            <input
              v-model="config.outputGroup.name"
              placeholder="e.g. Primary-HLS-Group"
              type="text"
              class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
              @input="updateJson"
            />
          </div>
          <div class="space-y-1.5" v-if="config.outputGroup.type === 'HLS'">
            <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Destination ID Ref</label>
            <div class="flex gap-2">
              <select
                v-model="hlsGroupSettings.Destination.DestinationRefId"
                class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                @change="updateJson"
              >
                <option value="FROM_TEMPLATE">FROM_TEMPLATE</option>
                <option v-for="dest in config.destinations" :key="dest.Id" :value="dest.Id">{{ dest.Id }}</option>
              </select>
            </div>
          </div>
          <div class="space-y-1.5" v-if="config.outputGroup.type === 'ARCHIVE'">
            <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Destination ID Ref</label>
            <select
              v-model="config.outputGroup.outputGroupSettings.ArchiveGroupSettings.Destination.DestinationRefId"
              class="w-full bg-black/20 border border-[#2a333d] rounded-sm px-4 py-2 text-sm text-[#ffaa00] font-mono focus:outline-none focus:border-[#ffaa00]/40 transition-all cursor-pointer"
              @change="updateJson"
            >
              <option value="FROM_TEMPLATE">FROM_TEMPLATE</option>
              <option v-for="dest in config.destinations" :key="dest.Id" :value="dest.Id">{{ dest.Id }}</option>
            </select>
          </div>
          <div class="space-y-1.5" v-if="config.outputGroup.type === 'ARCHIVE'">
            <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Rollover Interval (s) <i class="bi bi-info-circle ml-1" title="Number of seconds to write to archive file before closing and starting a new one."></i></label>
            <input
              v-model.number="config.outputGroup.outputGroupSettings.ArchiveGroupSettings.RolloverInterval"
              type="number"
              class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
              @input="updateJson"
            />
          </div>
          <div class="space-y-1.5" v-if="config.outputGroup.type === 'HLS'">
            <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Input Loss Action <i class="bi bi-info-circle ml-1" title="Input Loss Action: Documentation for input loss behavior."></i></label>
            <select
              v-model="hlsGroupSettings.InputLossAction"
              class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] cursor-pointer"
              @change="updateJson"
            >
              <option value="EMIT_OUTPUT">EMIT_OUTPUT</option>
              <option value="PAUSE_OUTPUT">PAUSE_OUTPUT</option>
            </select>
          </div>
        </div>

        <!-- Section: Destinations Mapping -->
        <div class="space-y-4 ">
          <div class="flex items-center justify-between mb-2">
            <h5 class="text-[10px] font-black text-[#cdd6ee] uppercase tracking-widest">Global Destinations</h5>
            <button type="button"
              v-if="config.destinations.length < 1"
              @click="addDestination"

              class="text-xs bg-[#ffaa00]/80 hover:bg-[#ffaa00] text-black! px-1 py-0.5 rounded-sm transition-all font-black uppercase tracking-tighter active:scale-95 border border-[#ffaa00]/20 leading-none"
            >
              <ma-plus class="bly-plus text-black! text-[8px]"></ma-plus> <span class="">Add Destination</span>
            </button>
          </div>

          <div v-for="(dest, dIdx) in config.destinations" :key="dIdx" class="grid grid-cols-12 gap-3 items-end bg-black/20 p-3 rounded-sm border border-[#2a333d] relative group/dest">
            <div class="col-span-11 space-y-3">
              <div class="flex items-baseline gap-3">
                <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1 whitespace-nowrap">Destination ID</label>
                <input v-model="dest.Id" type="text" placeholder="ID" class="flex-1 bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30" @focus="onDestinationIdFocus(dest.Id)" @input="onDestinationIdInput(dest, $event)" />
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div class="space-y-1">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">URL (Primary)</label>
                  <input v-model="dest.Settings[0].Url" type="text" placeholder="rtmp://..." class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @input="updateJson" />
                </div>
                <div class="space-y-1">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">URL (Secondary)</label>
                  <input v-model="dest.Settings[1].Url" type="text" placeholder="rtmp://..." class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @input="updateJson" />
                </div>
                <template v-if="config.outputGroup.type === 'RTMP'">
                <div class="space-y-1">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Stream Name (Primary)</label>
                  <input v-model="dest.Settings[0].StreamName" type="text" placeholder="stream-key" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @input="updateJson" />
                </div>
                <div class="space-y-1">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Stream Name (Secondary)</label>
                  <input v-model="dest.Settings[1].StreamName" type="text" placeholder="stream-key" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @input="updateJson" />
                </div>
                </template>
              </div>
            </div>
            <div class="col-span-1 flex justify-center pb-2">
              <button type="button" @click="removeDestination(dIdx)"  class="size-5 flex items-center justify-center  hover:text-red-600 text-white rounded-full transition-all shadow-lg active:scale-90 border border-red-500/20" title="Remove Destination">
                <i class="bi bi-x text-base text-white font-bold"></i>
              </button>
            </div>
          </div>
          <div v-if="config.destinations.length === 0" class="text-center py-4 border border-dashed border-[#2a333d] rounded-sm">
            <p class="text-[10px] text-[#cdd6ee] font-bold uppercase tracking-widest">No custom destinations added</p>
          </div>
        </div>

        <!-- RTMP Specific Group Settings -->
        <div v-if="config.outputGroup.type === 'RTMP'" class="space-y-4">
          <div class="grid grid-cols-2 gap-x-8 gap-y-5">
            <div class="space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Authentication Scheme <i class="bi bi-info-circle ml-1" title="Authentication scheme to use when connecting with CDN."></i></label>
              <select v-model="config.outputGroup.outputGroupSettings.RtmpGroupSettings.AuthenticationScheme" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                <option value="COMMON">COMMON</option>
                <option value="AKAMAI">AKAMAI</option>
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Cache Full Behavior <i class="bi bi-info-circle ml-1" title="Controls behavior when cache fills up."></i></label>
              <select v-model="config.outputGroup.outputGroupSettings.RtmpGroupSettings.CacheFullBehavior" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                <option value="DISCONNECT_IMMEDIATELY">DISCONNECT_IMMEDIATELY</option>
                <option value="WAIT_FOR_SERVER">WAIT_FOR_SERVER</option>
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Cache Length (sec) <i class="bi bi-info-circle ml-1" title="Cache length in seconds, in multiples of the segment length."></i></label>
              <input v-model.number="config.outputGroup.outputGroupSettings.RtmpGroupSettings.CacheLength" type="number" min="30" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Caption Data <i class="bi bi-info-circle ml-1" title="Controls the types of data that passes to onCaptionInfo outputs."></i></label>
              <select v-model="config.outputGroup.outputGroupSettings.RtmpGroupSettings.CaptionData" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                <option value="ALL">ALL</option>
                <option value="FIELD1_608">FIELD1_608</option>
                <option value="FIELD1_AND_FIELD2_608">FIELD1_AND_FIELD2_608</option>
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Input Loss Action <i class="bi bi-info-circle ml-1" title="Controls behavior of the RTMP output when the input signal is lost."></i></label>
              <select v-model="config.outputGroup.outputGroupSettings.RtmpGroupSettings.InputLossAction" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                <option value="EMIT_OUTPUT">EMIT_OUTPUT</option>
                <option value="PAUSE_OUTPUT">PAUSE_OUTPUT</option>
              </select>
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Restart Delay (sec) <i class="bi bi-info-circle ml-1" title="Number of seconds between retries after a disconnection."></i></label>
              <input v-model.number="config.outputGroup.outputGroupSettings.RtmpGroupSettings.RestartDelay" type="number" min="0" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Include Filler NAL Units <i class="bi bi-info-circle ml-1" title="Controls behavior when filler NAL units are present in the input."></i></label>
              <select v-model="config.outputGroup.outputGroupSettings.RtmpGroupSettings.IncludeFillerNalUnits" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                <option value="AUTO">AUTO</option>
                <option value="DROP">DROP</option>
                <option value="INCLUDE">INCLUDE</option>
              </select>
            </div>
            <div class="col-span-2 space-y-1.5">
              <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Ad Markers <i class="bi bi-info-circle ml-1" title="Controls ad marker output."></i></label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  :checked="config.outputGroup.outputGroupSettings.RtmpGroupSettings.AdMarkers && config.outputGroup.outputGroupSettings.RtmpGroupSettings.AdMarkers.includes('ON_CUE_POINT_SCTE35')"
                  @change="onRtmpAdMarkersChange($event)"
                  class="w-4 h-4 rounded border-[#2a333d] bg-black/20 text-[#ffaa00] cursor-pointer"
                />
                <span class="text-xs text-[#cdd6ee] font-black uppercase tracking-widest">ON_CUE_POINT_SCTE35</span>
              </label>
            </div>
          </div>
        </div>

        <!-- HLS Specific Group Settings -->
        <div v-if="config.outputGroup.type === 'HLS'" class="space-y-4">
          <!-- HLS Tabs -->
          <div
            role="radiogroup"
            aria-label="HLS output settings tabs"
            class="inline-flex flex-wrap gap-1 rounded-lg bg-black/70 p-1 overflow-x-auto no-scrollbar"
          >
            <button type="button"
              v-for="tab in tabs"
              :key="tab.id"
              role="radio"
              :aria-checked="activeTab === tab.id"
              class="rounded-sm px-2.5 py-1 text-xs font-medium tracking-[0.01em] transition-[background-color,color] duration-150 whitespace-nowrap"
              :class="activeTab === tab.id ? 'bg-[#ffaa00] text-black' : 'bg-transparent text-slate-300 hover:text-white'"
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class=" min-h-[240px]">
            <!-- Location Tab -->
            <div v-if="activeTab === 'location'" class="grid grid-cols-2 gap-x-8 gap-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div class="col-span-2 grid grid-cols-2 gap-6 bg-black/20 p-4 rounded-sm">
                <div class="space-y-4">
                  <div>
                    <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">Base URL Manifest</label>
                    <input v-model="hlsGroupSettings.BaseUrlManifest" type="text" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                  </div>
                  <div>
                    <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">Base URL Content</label>
                    <input v-model="hlsGroupSettings.BaseUrlContent" type="text" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                  </div>
                </div>
                <div class="space-y-4">
                  <div>
                    <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">Base URL Manifest B</label>
                    <input v-model="hlsGroupSettings.BaseUrlManifest1" type="text" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                  </div>
                  <div>
                    <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">Base URL Content B</label>
                    <input v-model="hlsGroupSettings.BaseUrlContent1" type="text" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                  </div>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">
                  Directory Layout <i class="bi bi-info-circle ml-1"></i>
                </label>
                <select v-model="hlsGroupSettings.DirectoryStructure" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="SINGLE_DIRECTORY">SINGLE_DIRECTORY</option>
                  <option value="SUBDIRECTORY_PER_STREAM">SUBDIRECTORY_PER_STREAM</option>
                </select>
              </div>
              <div v-if="hlsGroupSettings.DirectoryStructure === 'SUBDIRECTORY_PER_STREAM'" class="space-y-1.5 animate-in zoom-in-95 duration-200" title="Segments Per Subdirectory: Only applies if Directory Structure is set to Subdirectory Per Stream.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">
                  Segments Per Subdir <i class="bi bi-info-circle ml-1"></i>
                </label>
                <input v-model.number="hlsGroupSettings.SegmentsPerSubdirectory" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
              </div>
            </div>

            <!-- Manifest and Segments Tab -->
            <div v-if="activeTab === 'manifest'" class="grid grid-cols-2 gap-x-8 gap-y-5 animate-in fade-in duration-300">
              <div class="space-y-1.5" title="MANIFESTS_AND_SEGMENTS: Generates manifests (master manifest, if applicable, and media manifests) for this output group.

VARIANT_MANIFESTS_AND_SEGMENTS: Generates media manifests for this output group, but not a master manifest.

SEGMENTS_ONLY: Does not generate any manifests for this output group.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Output Selection <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.OutputSelection" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="MANIFESTS_AND_SEGMENTS">MANIFESTS_AND_SEGMENTS</option>
                  <option value="SEGMENTS_ONLY">SEGMENTS_ONLY</option>
                  <option value="VARIANT_MANIFESTS_AND_SEGMENTS">VARIANT_MANIFESTS_AND_SEGMENTS</option>
                </select>
              </div>
              <div class="space-y-1.5" title="If 'vod', all segments are indexed and kept permanently in the destination and manifest. If 'live', only the number segments specified in keepSegments and indexNSegments are kept.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Mode <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.Mode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="LIVE">LIVE</option>
                  <option value="VOD">VOD</option>
                </select>
              </div>
              <div class="space-y-1.5" title="SEGMENTED_FILES: Emit the program as segments - multiple .ts media files.

SINGLE_FILE: Applies only if Mode field is VOD. Emit the program as a single .ts media file.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">TS File Mode <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.TsFileMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="SEGMENTED_FILES">SEGMENTED_FILES</option>
                  <option value="SINGLE_FILE">SINGLE_FILE</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Includes or excludes Stream-Inf Resolution tag in .m3u8 manifest files.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Stream Inf Resolution <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.StreamInfResolution" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="INCLUDE">INCLUDE</option>
                  <option value="EXCLUDE">EXCLUDE</option>
                </select>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5" title="Length of MPEG-2 Transport Stream segments to create in seconds.">
                  <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Seg Length <i class="bi bi-info-circle ml-1"></i></label>
                  <input v-model.number="hlsGroupSettings.SegmentLength" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5" title="Minimum length of MPEG-2 Transport Stream segments in seconds.">
                  <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Min Seg <i class="bi bi-info-circle ml-1"></i></label>
                  <input v-model.number="hlsGroupSettings.MinSegmentLength" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5" title="Specifies the maximum number of segments in the media manifest file (LIVE mode only).">
                  <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Num Segs <i class="bi bi-info-circle ml-1"></i></label>
                  <input v-model.number="hlsGroupSettings.IndexNSegments" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5" title="Specifies the number of media segments to retain in the destination directory. Recommended: (2 x indexNsegments) + 1.">
                  <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Keep Segs <i class="bi bi-info-circle ml-1"></i></label>
                  <input v-model.number="hlsGroupSettings.KeepSegments" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
              </div>
              <div class="space-y-1.5" title="Specification to use during m3u8 playlist generation.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Codec Specification <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.CodecSpecification" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="RFC_4281">RFC_4281</option>
                  <option value="RFC_6381">RFC_6381</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Format for durations in the manifest.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Durations Format <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.ManifestDurationFormat" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="FLOATING_POINT">FLOATING_POINT</option>
                  <option value="INTEGER">INTEGER</option>
                </select>
              </div>
              <div class="space-y-1.5" title="DISABLED: Do not create an I-frame-only manifest. STANDARD: Create an I-frame-only manifest for each output that contains video.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">I-Frame Playlists <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.IFrameOnlyPlaylists" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="DISABLED">DISABLED</option>
                  <option value="STANDARD">STANDARD</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Includes or excludes EXT-X-PROGRAM-DATE-TIME tag in .m3u8 manifest files.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Program Date Time <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.ProgramDateTime" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="INCLUDE">INCLUDE</option>
                  <option value="EXCLUDE">EXCLUDE</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Period of insertion of EXT-X-PROGRAM-DATE-TIME entry, in seconds.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">PDT Period <i class="bi bi-info-circle ml-1"></i></label>
                <input v-model.number="hlsGroupSettings.ProgramDateTimePeriod" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
              </div>
              <div class="space-y-1.5" title="Specifies the algorithm used to drive the HLS EXT-X-PROGRAM-DATE-TIME clock.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">PDT Clock <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.ProgramDateTimeClock" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="INITIALIZE_FROM_OUTPUT_TIMECODE">INITIALIZE_FROM_OUTPUT_TIMECODE</option>
                  <option value="SYSTEM_CLOCK">SYSTEM_CLOCK</option>
                </select>
              </div>
              <div class="space-y-1.5" title="When set to 'disabled', sets the #EXT-X-ALLOW-CACHE:no tag in the manifest.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Client Cache <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.ClientCache" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="ENABLED">ENABLED</option>
                  <option value="DISABLED">DISABLED</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Timestamp delta in milliseconds.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Timestamp Delta <i class="bi bi-info-circle ml-1"></i></label>
                <input v-model.number="hlsGroupSettings.TimestampDeltaMilliseconds" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
              </div>
              <div class="space-y-1.5" title="Compress the manifest using GZIP.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Manifest Compression <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.ManifestCompression" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="NONE">NONE</option>
                  <option value="GZIP">GZIP</option>
                </select>
              </div>
              <div class="space-y-1.5" title="ENABLED: The master manifest includes information about both pipelines.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Redundant Manifest <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.RedundantManifest" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="DISABLED">DISABLED</option>
                  <option value="ENABLED">ENABLED</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Specifies whether to include the final (incomplete) segment when the pipeline stops.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Incomplete Seg Behavior <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.IncompleteSegmentBehavior" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="AUTO">AUTO</option>
                  <option value="SUPPRESS">SUPPRESS</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Specifies whether to insert EXT-X-DISCONTINUITY tags in the HLS child manifests.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Discontinuity Tags <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.DiscontinuityTags" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="INSERT">INSERT</option>
                  <option value="NEVER_INSERT">NEVER_INSERT</option>
                </select>
              </div>
              <div class="space-y-1.5" title="USE_SEGMENT_DURATION: Set the segment duration according to the segment duration. USE_INPUT_SEGMENTATION: Use input segmentation.">
                <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Segmentation Mode <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.SegmentationMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="USE_SEGMENT_DURATION">USE_SEGMENT_DURATION</option>
                  <option value="USE_INPUT_SEGMENTATION">USE_INPUT_SEGMENTATION</option>
                </select>
              </div>
            </div>

            <!-- Captions Tab -->
            <div v-if="activeTab === 'captions'" class="grid grid-cols-2 gap-4 animate-in fade-in duration-300">
              <div class="space-y-1.5" title="Caption Language Setting: Specifies whether to insert the caption language into the manifest.">
                <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">Caption Language Setting <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.CaptionLanguageSetting" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="OMIT">OMIT</option>
                  <option value="INSERT">INSERT</option>
                  <option value="NONE">NONE</option>
                </select>
              </div>
            </div>

            <!-- ID3 Tab -->
            <div v-if="activeTab === 'id3'" class="grid grid-cols-2 gap-x-8 gap-y-5 animate-in fade-in duration-300">
              <div class="space-y-1.5" title="ID3 Frame Type: Specifies the type of ID3 frame to insert.">
                <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">ID3 Frame Type <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.TimedMetadataId3Frame" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="PRIV">PRIV</option>
                  <option value="NONE">NONE</option>
                  <option value="TDRL">TDRL</option>
                </select>
              </div>
              <div class="space-y-1.5" title="Timed Metadata ID3 Period: Period of insertion of ID3 frame, in seconds.">
                <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">ID3 Period (sec) <i class="bi bi-info-circle ml-1"></i></label>
                <input v-model.number="hlsGroupSettings.TimedMetadataId3Period" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
              </div>
              <div class="space-y-1.5" title="HLS ID3 Segment Tagging: Specifies whether to include ID3 segment tagging.">
                <label class="block text-[10px] text-[#cdd6ee] mb-1.5 uppercase font-black tracking-widest">HLS ID3 Tagging <i class="bi bi-info-circle ml-1"></i></label>
                <select v-model="hlsGroupSettings.HlsId3SegmentTagging" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                  <option value="DISABLED">DISABLED</option>
                  <option value="ENABLED">ENABLED</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr class="border-[#2a333d]">

      <!-- Section: Video Descriptions -->
      <section class="space-y-4">
        <div class="flex items-center justify-between px-1">
          <div class="flex items-center gap-3">

            <span class="text-[10px] font-black text-[#cdd6ee] uppercase tracking-widest">Video Descriptions</span>
            <span class="bg-[#ffaa00]/15 text-[#ffaa00] text-[10px] px-2 py-0.5 rounded-full font-black border border-[#ffaa00]/20">{{ config.videoDescriptions.length }}</span>
            <div class="h-px flex-1 bg-black/20"></div>
          </div>
          <button type="button"
            v-if="config.videoDescriptions.length < 1"
            @click="addVideoDescription"

            class="group flex items-center gap-1 text-xs bg-[#ffaa00]/80 hover:bg-[#ffaa00] text-black! font-black uppercase tracking-tighter px-1 py-0.5 rounded-sm transition-all active:scale-95 ml-4 whitespace-nowrap border border-[#ffaa00]/20"
          >
            <ma-plus class="bly-plus transition-transform text-black! text-[8px]"></ma-plus>
            <span class="">Add Video Desc</span>
          </button>
        </div>

        <div class="grid gap-6">
          <div v-for="(video, vIdx) in config.videoDescriptions" :key="vIdx"
               class="bg-black/30 rounded-sm  overflow-hidden relative group/video  hover:bg-black/30 transition-all duration-500">

            <!-- Collapsible Header -->
            <div
              @click="toggleVideoMinimize(video, $event)"
              class="px-6 py-3 bg-black/20 flex items-center justify-between cursor-pointer hover:bg-black/20 transition-colors"
            >
              <div class="flex items-center gap-4">
                <!-- <i class="bi" :class="video.isMinimized ? 'bi-chevron-right' : 'bi-chevron-down'"></i> -->
                <ma-chevron-down class="w-3 h-3" :class="video.isMinimized ? 'rotate-0' : 'rotate-180'"></ma-chevron-down>
                <div class="flex items-center gap-3">
                  <span class="text-xs font-bold text-[#ffaa00] font-mono">{{ video.Name || 'Unnamed Video' }}</span>
                  <span class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest bg-black/20 px-2 py-0.5 rounded border border-[#2a333d]">{{ video.Width }}x{{ video.Height }}</span>
                  <span class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest bg-black/20 px-2 py-0.5 rounded border border-[#2a333d]">{{ (video.codecType || 'No Codec').replace('Settings', '') }}</span>
                </div>
              </div>
              <button type="button"
                @click.stop="removeVideoDescription(vIdx)"
                class="size-5 flex items-center justify-center  hover:text-red-600 text-white rounded-full transition-all shadow-lg active:scale-90"
                title="Delete Video Description"
              >
                <ma-delete class="text-sm ma-btn"></ma-delete>
              </button>
            </div>

            <div v-show="!video.isMinimized" class="p-6">
              <div class="grid grid-cols-4 gap-6 mb-6">
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Name (ID) <span class="text-red-500">*</span>
                  </label>
                  <input v-model="video.Name" type="text" placeholder="video_1080p30" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" :class="{'border-red-500/50': !video.Name || video.Name === 'None'}" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Width</label>
                  <input v-model.number="video.Width" type="number" placeholder="1920" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Height</label>
                  <input v-model.number="video.Height" type="number" placeholder="1080" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Codec <span class="text-red-500">*</span>
                  </label>
                  <select v-model="video.codecType" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" :class="{'border-red-500/50': !video.codecType || video.codecType === 'None' || video.codecType === 'NONE'}" @change="onCodecTypeChange(video)">
                    <option :value="null">None</option>
                    <option value="H264Settings">H.264</option>
                    <option v-if="config.outputGroup.type !== 'RTMP'" value="H265Settings">H.265 (HEVC)</option>
                    <option v-if="config.outputGroup.type !== 'ARCHIVE' && config.outputGroup.type !== 'RTMP'" value="FrameCaptureSettings">Frame Capture</option>
                  </select>
                </div>
              </div> <!-- Close video header grid -->

            <!-- Scaling Settings Sub-Section -->
            <div class="bg-black/20 p-4 rounded-sm border border-[#2a333d] mb-6">
              <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest mb-4 flex items-center gap-2">
                <i class="bi bi-aspect-ratio text-[#ffaa00]"></i> Scaling Settings
              </div>
              <div class="grid grid-cols-3 gap-6">
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Scaling Behavior</label>
                  <select v-model="video.ScalingBehavior" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                    <option value="DEFAULT">DEFAULT</option>
                    <option value="STRETCH_TO_OUTPUT">STRETCH_TO_OUTPUT</option>
                  </select>
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Sharpness (0-100)</label>
                  <input v-model.number="video.Sharpness" type="number" min="0" max="100" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] outline-none focus:border-[#ffaa00]/30" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Respond to AFD</label>
                  <select v-model="video.RespondToAfd" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                    <option value="NONE">NONE</option>
                    <option value="PASSTHROUGH">PASSTHROUGH</option>
                    <option value="RESPOND">RESPOND</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Codec Settings TABS (H264 / H265 / Frame Capture) -->
            <div v-if="['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-4">
              <div role="radiogroup" aria-label="Codec settings tabs" class="inline-flex flex-wrap gap-1 rounded-lg bg-black/70 p-1 overflow-x-auto no-scrollbar">
                <button type="button"
                  role="radio"
                  :aria-checked="(video.activeCodecTab || 'ratio') === cTab.id"
                  v-for="cTab in codecTabs"
                  :key="cTab.id"
                  class="rounded-sm px-2.5 py-1 text-xs font-medium tracking-[0.01em] transition-[background-color,color] duration-150 whitespace-nowrap"
                  :class="(video.activeCodecTab || 'ratio') === cTab.id ? 'bg-[#ffaa00] text-black' : 'bg-transparent text-slate-300 hover:text-white'"
                  @click.stop="setVideoTab(video, cTab.id)"
                >
                  {{ cTab.label }}
                </button>
              </div>

              <div v-if="['H265Settings', 'H264Settings'].includes(video.codecType)" class="bg-black/20 p-5 rounded-sm border border-[#2a333d] min-h-[200px]">
                <!-- Aspect Ratio Tab (H265/H264) -->
                <div v-if="(video.activeCodecTab || 'ratio') === 'ratio'" class="grid grid-cols-2 gap-6 animate-in fade-in duration-200">
                  <div v-if="video.codecType === 'H264Settings'" class="space-y-1.5 col-span-2">
                    <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">PAR Control <i class="bi bi-info-circle ml-1" title="Indicates how the output aspect ratio is determined."></i></label>
                    <select v-model="video.CodecSettings[video.codecType].ParControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                      <option value="INITIALIZE_FROM_SOURCE">INITIALIZE_FROM_SOURCE</option>
                      <option value="SPECIFIED">SPECIFIED</option>
                    </select>
                  </div>
                  <div class="space-y-1.5">
                    <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">PAR Numerator <i class="bi bi-info-circle ml-1" title="Pixel Aspect Ratio numerator."></i></label>
                    <input v-model.number="video.CodecSettings[video.codecType].ParNumerator" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                  </div>
                  <div class="space-y-1.5">
                    <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">PAR Denominator <i class="bi bi-info-circle ml-1" title="Pixel Aspect Ratio denominator."></i></label>
                    <input v-model.number="video.CodecSettings[video.codecType].ParDenominator" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                  </div>
                  <div class="space-y-1.5">
                    <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">AFD Signaling <i class="bi bi-info-circle ml-1" title="Active Format Description (AFD) signaling."></i></label>
                    <select v-model="video.CodecSettings[video.codecType].AfdSignaling" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                      <option value="NONE">NONE</option>
                      <option value="AUTO">AUTO</option>
                      <option value="FIXED">FIXED</option>
                    </select>
                  </div>
                  <div v-if="video.CodecSettings[video.codecType] && video.CodecSettings[video.codecType].AfdSignaling === 'FIXED'" class="space-y-1.5 animate-in slide-in-from-top-2 duration-300">
                    <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Fixed AFD <i class="bi bi-info-circle ml-1" title="Fixed AFD value when AFD signaling is set to FIXED."></i></label>
                    <select v-model="video.CodecSettings[video.codecType].FixedAfd" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] border-[#ffaa00]/40" @change="updateJson">
                      <option v-for="afd in afdOptions" :key="afd" :value="afd.split(' ')[0]">{{ afd }}</option>
                    </select>
                  </div>
                </div>

                <!-- Rate Control Tab (H265/H264) -->
                <div v-if="video.activeCodecTab === 'rate' && ['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-6 animate-in fade-in duration-200">
                  <div class="grid grid-cols-3 gap-6">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Rate Control Mode <i class="bi bi-info-circle ml-1" title="Selects the algorithm used for cost-based rate control."></i></label>
                      <select v-model="video.CodecSettings[video.codecType].RateControlMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="CBR">CBR</option>
                        <option value="VBR">VBR</option>
                        <option value="QVBR">QVBR</option>
                        <option v-if="video.codecType === 'H264Settings'" value="MULTIPLEX">MULTIPLEX</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitrate <i class="bi bi-info-circle ml-1" title="Average bitrate in bits/second."></i></label>
                      <input v-model.number="video.CodecSettings[video.codecType].Bitrate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Min QP <i class="bi bi-info-circle ml-1" title="Minimum Quantization Parameter."></i></label>
                      <input v-model.number="video.CodecSettings[video.codecType].MinQp" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                  </div>

                  <div class="pt-4 border-t border-[#2a333d]">
                    <span class="text-xs font-black text-[#cdd6ee] uppercase tracking-widest mb-3 block">Additional Rate Control Settings</span>
                    <div class="grid grid-cols-5 gap-4">
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Max Bitrate</label>
                        <input v-model.number="video.CodecSettings[video.codecType].MaxBitrate" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Min Bitrate</label>
                        <input v-model.number="video.CodecSettings[video.codecType].MinBitrate" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Buffer Size</label>
                        <input v-model.number="video.CodecSettings[video.codecType].BufSize" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div v-if="video.codecType === 'H264Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Buf Fill %</label>
                        <input v-model.number="video.CodecSettings.H264Settings.BufFillPct" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">QVBR Level</label>
                        <input v-model.number="video.CodecSettings[video.codecType].QvbrQualityLevel" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="video.activeCodecTab === 'frame' && ['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-6 animate-in fade-in duration-200">
                  <div class="grid grid-cols-2 gap-x-8 gap-y-6">
                    <div v-if="video.codecType === 'H264Settings'" class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Framerate Control <i class="bi bi-info-circle ml-1" title="Selects the algorithm used for frame rate control."></i></label>
                      <select v-model="video.CodecSettings[video.codecType].FramerateControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="INITIALIZE_FROM_SOURCE">INITIALIZE_FROM_SOURCE</option>
                        <option value="SPECIFIED">SPECIFIED</option>
                      </select>
                    </div>
                    <div v-if="video.codecType === 'H264Settings'" class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Force Field Pictures <i class="bi bi-info-circle ml-1" title="Enables or disables forced interlaced field pictures."></i></label>
                      <select v-model="video.CodecSettings.H264Settings.ForceFieldPictures" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="ENABLED">ENABLED</option>
                        <option value="DISABLED">DISABLED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest flex items-center gap-1">Numerator <i class="bi bi-info-circle ml-1" title="Framerate numerator."></i><span v-if="!['H264Settings'].includes(video.codecType)" class="text-red-500">*</span></label>
                      <input v-model.number="video.CodecSettings[video.codecType].FramerateNumerator" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" :class="!['H264Settings'].includes(video.codecType) && !video.CodecSettings[video.codecType].FramerateNumerator ? 'border-red-500/50' : 'border-[#2a333d]'" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest flex items-center gap-1">Denominator <i class="bi bi-info-circle ml-1" title="Framerate denominator."></i><span v-if="!['H264Settings'].includes(video.codecType)" class="text-red-500">*</span></label>
                      <input v-model.number="video.CodecSettings[video.codecType].FramerateDenominator" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" :class="!['H264Settings'].includes(video.codecType) && !video.CodecSettings[video.codecType].FramerateDenominator ? 'border-red-500/50' : 'border-[#2a333d]'" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Scan Type <i class="bi bi-info-circle ml-1" title="Indicates if the output is progressive or interlaced."></i></label>
                      <select v-model="video.CodecSettings[video.codecType].ScanType" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="PROGRESSIVE">PROGRESSIVE</option>
                        <option value="INTERLACED">INTERLACED</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- GOP Structure Tab (H265/H264) -->
                <div v-if="video.activeCodecTab === 'gop' && ['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-6 animate-in fade-in duration-200">
                  <div class="grid grid-cols-3 gap-6">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">GOP Size <i class="bi bi-info-circle ml-1" title="GOP size (distance between IDR frames)."></i></label>
                      <input v-model.number="video.CodecSettings[video.codecType].GopSize" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">GOP Size Units <i class="bi bi-info-circle ml-1" title="Units for GOP size."></i></label>
                      <select v-model="video.CodecSettings[video.codecType].GopSizeUnits" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="FRAMES">FRAMES</option>
                        <option value="SECONDS">SECONDS</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Num B-Frames <i class="bi bi-info-circle ml-1" title="Number of B-frames between reference frames."></i></label>
                      <input v-model.number="video.CodecSettings[video.codecType].GopNumBFrames" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                  </div>
                  <div class="pt-4 border-t border-[#2a333d] text-left">
                    <span class="text-xs font-black text-[#cdd6ee] uppercase tracking-widest mb-3 block">Additional GOP Settings</span>
                    <div class="grid grid-cols-4 gap-4">
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Closed GOP Cadence</label>
                        <input v-model.number="video.CodecSettings[video.codecType].GopClosedCadence" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div v-if="video.codecType === 'H264Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Num Ref Frames</label>
                        <input v-model.number="video.CodecSettings.H264Settings.NumRefFrames" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">B-Frame Reference</label>
                        <select v-model="video.CodecSettings[video.codecType].GopBReference" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="ENABLED">ENABLED</option>
                          <option value="DISABLED">DISABLED</option>
                        </select>
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Scene Change Detect</label>
                        <select v-model="video.CodecSettings[video.codecType].SceneChangeDetect" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="ENABLED">ENABLED</option>
                          <option value="DISABLED">DISABLED</option>
                        </select>
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Min I-Interval</label>
                        <input v-model.number="video.CodecSettings[video.codecType].MinIInterval" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Sub-GOP Length</label>
                        <select v-model="video.CodecSettings[video.codecType].SubgopLength" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="FIXED">FIXED</option>
                          <option value="DYNAMIC">DYNAMIC</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Codec Details Tab (H265/H264) -->
                <div v-if="video.activeCodecTab === 'details' && ['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-6 animate-in fade-in duration-200">
                  <div class="grid grid-cols-4 gap-6">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Profile <i class="bi bi-info-circle ml-1" title="H.264/H.265 profile selection."></i></label>
                      <select v-model="video.CodecSettings[video.codecType].Profile" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <template v-if="video.codecType === 'H265Settings'">
                          <option value="MAIN">MAIN</option>
                          <option value="MAIN_10BIT">MAIN_10BIT</option>
                        </template>
                        <template v-else>
                          <option v-for="p in h264Profiles" :key="p" :value="p">{{ p }}</option>
                        </template>
                      </select>
                    </div>
                    <div v-if="video.codecType === 'H265Settings'" class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Tier <i class="bi bi-info-circle ml-1" title="H.265 tier selection."></i></label>
                      <select v-model="video.CodecSettings.H265Settings.Tier" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="MAIN">MAIN</option>
                        <option value="HIGH">HIGH</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Level <i class="bi bi-info-circle ml-1" title="Codec level selection."></i></label>
                      <select v-model="video.CodecSettings[video.codecType].Level" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <template v-if="video.codecType === 'H265Settings'">
                          <option value="H265_LEVEL_AUTO">AUTO</option>
                          <option v-for="lvl in h265Levels" :key="lvl" :value="lvl">{{ lvl }}</option>
                        </template>
                        <template v-else>
                          <option value="H264_LEVEL_AUTO">AUTO</option>
                          <option v-for="lvl in h264Levels" :key="lvl" :value="lvl">{{ lvl }}</option>
                        </template>
                      </select>
                    </div>
                    <div v-if="video.codecType === 'H265Settings'" class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Deblocking <i class="bi bi-info-circle ml-1" title="Enables or disables deblocking filter."></i></label>
                      <select v-model="video.CodecSettings.H265Settings.Deblocking" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="ENABLED">ENABLED</option>
                        <option value="DISABLED">DISABLED</option>
                      </select>
                    </div>
                  </div>
                  <div class="pt-4 border-t border-[#2a333d] text-left">
                    <span class="text-xs font-black text-[#cdd6ee] uppercase tracking-widest mb-3 block">Advanced Codec Details</span>
                    <div class="grid grid-cols-4 gap-4">
                      <div v-if="video.codecType === 'H264Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Entropy Encoding</label>
                        <select v-model="video.CodecSettings.H264Settings.EntropyEncoding" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="CABAC">CABAC</option>
                          <option value="CAVLC">CAVLC</option>
                        </select>
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Slices</label>
                        <input v-model.number="video.CodecSettings[video.codecType].Slices" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div v-if="video.codecType === 'H265Settings'" class="grid grid-cols-2 gap-2 col-span-2">
                        <div class="space-y-1">
                          <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Tile Width</label>
                          <input v-model.number="video.CodecSettings.H265Settings.TileWidth" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                        </div>
                        <div class="space-y-1">
                          <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Tile Height</label>
                          <input v-model.number="video.CodecSettings.H265Settings.TileHeight" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                        </div>
                      </div>
                      <div v-if="video.codecType === 'H265Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Tile Padding</label>
                        <select v-model="video.CodecSettings.H265Settings.TilePadding" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="NONE">NONE</option>
                          <option value="PADDED">PADDED</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H265Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Mv Temporal Predictor</label>
                        <select v-model="video.CodecSettings.H265Settings.MvTemporalPredictor" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="ENABLED">ENABLED</option>
                          <option value="DISABLED">DISABLED</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H265Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Mv Over Pix Bound</label>
                        <select v-model="video.CodecSettings.H265Settings.MvOverPictureBoundaries" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="ENABLED">ENABLED</option>
                          <option value="DISABLED">DISABLED</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H265Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Treeblock Size</label>
                        <select v-model="video.CodecSettings.H265Settings.TreeblockSize" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="AUTO">AUTO</option>
                          <option value="TREE_SIZE_32X32">32X32</option>
                        </select>
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Adaptive Quant</label>
                        <select v-model="video.CodecSettings[video.codecType].AdaptiveQuantization" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="AUTO">AUTO</option>
                          <option value="HIGH">HIGH</option>
                          <option v-if="video.codecType === 'H265Settings'" value="HIGHER">HIGHER</option>
                          <option value="LOW">LOW</option>
                          <option value="OFF">OFF</option>
                          <option v-if="video.codecType === 'H264Settings'" value="MAX">MAX</option>
                          <option v-if="video.codecType === 'H264Settings'" value="MEDIUM">MEDIUM</option>
                        </select>
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Flicker AQ</label>
                        <select v-model="video.CodecSettings[video.codecType].FlickerAq" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="ENABLED">ENABLED</option>
                          <option value="DISABLED">DISABLED</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H264Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Spatial AQ</label>
                        <select v-model="video.CodecSettings.H264Settings.SpatialAq" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="ENABLED">ENABLED</option>
                          <option value="DISABLED">DISABLED</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H264Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Temporal AQ</label>
                        <select v-model="video.CodecSettings.H264Settings.TemporalAq" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="ENABLED">ENABLED</option>
                          <option value="DISABLED">DISABLED</option>
                        </select>
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Look Ahead RC</label>
                        <select v-model="video.CodecSettings[video.codecType].LookAheadRateControl" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="HIGH">HIGH</option>
                          <option value="MEDIUM">MEDIUM</option>
                          <option value="LOW">LOW</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H265Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Alt Transfer Func</label>
                        <select v-model="video.CodecSettings.H265Settings.AlternativeTransferFunction" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="OMIT">OMIT</option>
                          <option value="INSERT">INSERT</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H264Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Syntax</label>
                        <select v-model="video.CodecSettings.H264Settings.Syntax" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="DEFAULT">DEFAULT</option>
                          <option value="RP2027">RP2027</option>
                        </select>
                      </div>
                      <div v-if="video.codecType === 'H264Settings'" class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Softness</label>
                        <input v-model.number="video.CodecSettings.H264Settings.Softness" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1.5 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1">
                        <label class="text-[9px] text-[#cdd6ee] uppercase font-bold">Color Metadata</label>
                        <select v-model="video.CodecSettings[video.codecType].ColorMetadata" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-2 py-1 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="INSERT">INSERT</option>
                          <option value="IGNORE">IGNORE</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Timecode Tab (H265/H264) -->
                <div v-if="video.activeCodecTab === 'timecode' && ['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-6 animate-in fade-in duration-200">
                  <div class="grid grid-cols-2 gap-6">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Timecode Insertion <i class="bi bi-info-circle ml-1" title="Selects the timecode insertion mode."></i></label>
                      <select v-model="video.CodecSettings[video.codecType].TimecodeInsertion" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="DISABLED">DISABLED</option>
                        <option value="PIC_TIMING_SEI">PIC_TIMING_SEI</option>
                      </select>
                    </div>
                  </div>

                  <!-- Timecode Burn-in Settings -->
                  <div class="pt-4 border-t border-[#2a333d]">
                    <div class="flex items-center justify-between mb-4">
                      <span class="text-xs font-black text-[#cdd6ee] uppercase tracking-widest">Timecode Burn-in Settings</span>
                    <button type="button"
                      @click="toggleTimecodeBurnin(video)"
                      class="text-xs font-black uppercase tracking-widest px-2 py-0.5 rounded border transition-all"
                      :class="video.CodecSettings[video.codecType].TimecodeBurninSettings ? 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20' : 'bg-[#ffaa00]/15 text-[#ffaa00] border-[#ffaa00]/20 hover:bg-[#ffaa00]/80'"
                    >
                      {{ video.CodecSettings[video.codecType].TimecodeBurninSettings ? 'Remove' : 'Add Burn-in' }}
                    </button>
                    </div>

                    <div v-if="video.CodecSettings[video.codecType].TimecodeBurninSettings" class="grid grid-cols-3 gap-6 animate-in slide-in-from-top-2 duration-200">
                      <div class="space-y-1.5">
                        <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Font Size</label>
                        <select v-model="video.CodecSettings[video.codecType].TimecodeBurninSettings.FontSize" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-2 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="EXTRA_SMALL_10">EXTRA_SMALL_10</option>
                          <option value="SMALL_16">SMALL_16</option>
                          <option value="MEDIUM_32">MEDIUM_32</option>
                          <option value="LARGE_48">LARGE_48</option>
                        </select>
                      </div>
                      <div class="space-y-1.5">
                        <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Position</label>
                        <select v-model="video.CodecSettings[video.codecType].TimecodeBurninSettings.Position" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-2 text-xs text-[#cdd6ee] outline-none" @change="updateJson">
                          <option value="TOP_LEFT">TOP_LEFT</option>
                          <option value="TOP_CENTER">TOP_CENTER</option>
                          <option value="TOP_RIGHT">TOP_RIGHT</option>
                          <option value="MIDDLE_LEFT">MIDDLE_LEFT</option>
                          <option value="MIDDLE_CENTER">MIDDLE_CENTER</option>
                          <option value="MIDDLE_RIGHT">MIDDLE_RIGHT</option>
                          <option value="BOTTOM_LEFT">BOTTOM_LEFT</option>
                          <option value="BOTTOM_CENTER">BOTTOM_CENTER</option>
                          <option value="BOTTOM_RIGHT">BOTTOM_RIGHT</option>
                        </select>
                      </div>
                      <div class="space-y-1.5">
                        <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Prefix</label>
                        <input v-model="video.CodecSettings[video.codecType].TimecodeBurninSettings.Prefix" type="text" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-2 text-xs text-[#cdd6ee] outline-none" @input="updateJson" />
                      </div>
                    </div>
                    <div v-else class="py-4 text-center border border-dashed border-[#2a333d] rounded-sm">
                      <p class="text-[9px] text-[#cdd6ee] font-bold uppercase tracking-widest italic">Timecode Burn-in is disabled</p>
                    </div>
                  </div>
                </div>

                <!-- Additional Encoding Settings Tab (H265/H264) -->
                <div v-if="video.activeCodecTab === 'additional' && ['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-4 animate-in fade-in duration-200">
                  <div class="grid grid-cols-2 gap-4">
                    <!-- Quality Level (H264 ONLY) -->
                    <div v-if="video.codecType === 'H264Settings'" class="space-y-1.5 px-1">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-tight">Quality Level <i class="bi bi-info-circle ml-1" title="Selects the quality level for the output."></i></label>
                      <select v-model="video.CodecSettings.H264Settings.QualityLevel" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#e5eefc] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                        <option value="STANDARD_QUALITY">STANDARD_QUALITY</option>
                        <option value="ENHANCED_QUALITY">ENHANCED_QUALITY</option>
                      </select>
                    </div>

                    <div class="space-y-1.5 px-1" :class="video.codecType !== 'H264Settings' ? 'col-span-2' : ''">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-tight">Filter Settings Type</label>
                      <select v-model="video.activeFilterType" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#e5eefc] outline-none focus:border-[#ffaa00]/30" @change="onFilterTypeChange(video)">
                        <option :value="null">None</option>
                        <option value="TemporalFilterSettings">Temporal Filter</option>
                        <option value="BandwidthReductionFilterSettings">Bandwidth Reduction Filter</option>
                      </select>
                    </div>

                    <!-- Temporal Filter Settings -->
                    <div v-if="video.activeFilterType === 'TemporalFilterSettings' && video.CodecSettings[video.codecType].FilterSettings && video.CodecSettings[video.codecType].FilterSettings.TemporalFilterSettings" class="col-span-2 space-y-3 bg-black/20 p-3 rounded-sm border border-[#2a333d]">
                      <span class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-2">
                        <i class="bi bi-filter-circle text-[#ffaa00]"></i> Temporal Filter Settings
                      </span>
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5">
                          <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-tight">Post Filter Sharpening</label>
                          <select v-model="video.CodecSettings[video.codecType].FilterSettings.TemporalFilterSettings.PostFilterSharpening" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                            <option value="AUTO">AUTO</option>
                            <option value="DISABLED">DISABLED</option>
                            <option value="ENABLED">ENABLED</option>
                          </select>
                        </div>
                        <div class="space-y-1.5">
                          <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-tight">Strength</label>
                          <select v-model="video.CodecSettings[video.codecType].FilterSettings.TemporalFilterSettings.Strength" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                            <option v-for="s in temporalStrengths" :key="s" :value="s">{{ s }}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <!-- Bandwidth Reduction Filter Settings -->
                    <div v-if="video.activeFilterType === 'BandwidthReductionFilterSettings' && video.CodecSettings[video.codecType].FilterSettings && video.CodecSettings[video.codecType].FilterSettings.BandwidthReductionFilterSettings" class="col-span-2 space-y-3 bg-black/20 p-3 rounded-sm border border-[#2a333d]">
                      <span class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-2">
                        <i class="bi bi-wifi-off text-[#ffaa00]"></i> Bandwidth Reduction Filter Settings
                      </span>
                      <div class="grid grid-cols-2 gap-4">
                        <div class="space-y-1.5">
                          <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-tight">Post Filter Sharpening</label>
                          <select v-model="video.CodecSettings[video.codecType].FilterSettings.BandwidthReductionFilterSettings.PostFilterSharpening" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                            <option v-for="s in bandwidthSharpening" :key="s" :value="s">{{ s }}</option>
                          </select>
                        </div>
                        <div class="space-y-1.5">
                          <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest leading-tight">Strength</label>
                          <select v-model="video.CodecSettings[video.codecType].FilterSettings.BandwidthReductionFilterSettings.Strength" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                            <option v-for="s in bandwidthStrengths" :key="s" :value="s">{{ s }}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="video.activeCodecTab === 'colorspace' && ['H265Settings', 'H264Settings'].includes(video.codecType)" class="space-y-6 animate-in fade-in duration-200">
                  <div class="grid grid-cols-2 gap-6">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Color Space <i class="bi bi-info-circle ml-1" title="Select the color space for this video description."></i></label>
                      <select v-model="video.activeColorSpace" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="onColorSpaceChange(video)">
                        <option :value="null">None</option>
                        <option value="ColorSpacePassthroughSettings">Color Space Passthrough</option>
                        <option value="Rec601Settings">Rec. 601</option>
                        <option value="Rec709Settings">Rec. 709</option>
                        <template v-if="video.codecType === 'H265Settings'">
                          <option value="Hdr10Settings">HDR10</option>
                          <option value="Hlg2020Settings">HLG 2020</option>
                          <option value="DolbyVision81Settings">Dolby Vision 8.1</option>
                        </template>
                      </select>
                    </div>

                    <div v-if="video.activeColorSpace === 'Hdr10Settings' && video.CodecSettings.H265Settings.ColorSpaceSettings && video.CodecSettings.H265Settings.ColorSpaceSettings.Hdr10Settings" class="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2">
                       <div class="space-y-1.5">
                        <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Max CLL</label>
                        <input v-model.number="video.CodecSettings.H265Settings.ColorSpaceSettings.Hdr10Settings.MaxCll" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1.5">
                        <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Max FALL</label>
                        <input v-model.number="video.CodecSettings.H265Settings.ColorSpaceSettings.Hdr10Settings.MaxFall" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] outline-none" @input="updateJson" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div> <!-- Added missing div for grid-cols-4 -->
            </div> <!-- Close show!video.isMinimized -->
          </div> <!-- Close v-for videoDescriptions -->
        </div> <!-- Close grid gap-6 for video descriptions -->
      </section>

      <!-- Section: Audio Descriptions -->
      <section class="space-y-4">
        <div class="flex items-center justify-between px-1">
          <div class="flex items-center gap-3">

            <span class="text-[10px] font-black text-[#cdd6ee] uppercase tracking-widest">Audio Descriptions</span>
            <span class="bg-[#ffaa00]/15 text-[#ffaa00] text-[10px] px-2 py-0.5 rounded-full font-black border border-[#ffaa00]/20">{{ config.audioDescriptions.length }}</span>
            <div class="h-px flex-1 bg-black/20"></div>
          </div>
          <button type="button"
            @click="addAudioDescription"
            class="group flex items-center gap-1 text-xs bg-[#ffaa00]/80 hover:bg-[#ffaa00] text-black font-black uppercase tracking-tighter px-1 py-0.5 rounded-sm transition-all active:scale-95 ml-4 whitespace-nowrap border border-[#ffaa00]/20"
          >
            <ma-plus class="bly-plus transition-transform text-black! text-[8px]"></ma-plus>
            <span class="text-black">Add Audio Desc</span>
          </button>
        </div>

        <div class="grid gap-6">
          <div v-for="(audio, aIdx) in config.audioDescriptions" :key="aIdx"
               class="bg-black/15 rounded-sm  overflow-hidden relative group/audio  transition-all duration-500">

            <!-- Collapsible Header -->
            <div
              @click="toggleAudioMinimize(audio, $event)"
              class="px-6 py-3 bg-black/20 flex items-center justify-between cursor-pointer hover:bg-black/20 transition-colors"
            >
              <div class="flex items-center gap-4">
                <!-- <i class="bi" :class="audio.isMinimized ? 'bi-chevron-right' : 'bi-chevron-down'"></i> -->
                <ma-chevron-down class="w-3 h-3" :class="audio.isMinimized ? 'rotate-0' : 'rotate-180'"></ma-chevron-down>
                <div class="flex items-center gap-3">
                  <span class="text-xs font-bold text-[#ffaa00] font-mono">{{ audio.AudioSelectorName || 'Unnamed Audio' }}</span>
                  <span class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest bg-black/20 px-2 py-0.5 rounded border border-[#2a333d]">{{ audio.Name }}</span>
                  <span class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest bg-black/20 px-2 py-0.5 rounded border border-[#2a333d]">{{ (audio.codecType || 'No Codec').replace('Settings', '') }}</span>
                </div>
              </div>
              <button type="button"
                @click.stop="removeAudioDescription(aIdx)"
                class="size-5 flex items-center justify-center  hover:text-red-600 text-white rounded-full transition-all shadow-lg active:scale-90"
                title="Delete Audio Description"
              >
                <ma-delete class="text-sm ma-btn"></ma-delete>
              </button>
            </div>

            <div v-show="!audio.isMinimized" class="p-6">
              <div class="grid grid-cols-4 gap-6 mb-8 sticky top-0 bg-black/30 p-5 rounded-sm border border-[#ffaa00]/20 z-10 backdrop-blur-md shadow-2xl">
                <div class="space-y-1.5 focus-within:z-20">
                  <label class="text-[10px] text-[#ffaa00] uppercase font-black tracking-widest ml-1 flex items-center gap-2">
                    <i class="bi bi-mic-fill"></i> Audio Desc Name <span class="text-red-500">*</span>
                  </label>
                  <input v-model="audio.Name" type="text" placeholder="audio_eng" class="w-full bg-black/20 border border-[#2a333d] rounded-sm px-4 py-2.5 text-sm text-white focus:ring-2 focus:ring-[#ffaa00]/20 focus:border-[#ffaa00] transition-all font-bold outline-none shadow-inner" :class="{'border-red-500/50': !audio.Name || audio.Name === 'None'}" @input="updateJson" />
                </div>
                <div class="space-y-1.5 focus-within:z-20">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1 flex items-center gap-1">Audio Selector <span class="text-red-500">*</span></label>
                  <input v-model="audio.AudioSelectorName" type="text" placeholder="None" class="w-full bg-black/20 border border-[#2a333d] rounded-sm px-4 py-2.5 text-sm text-[#e5eefc] focus:border-[#ffaa00]/30 outline-none transition-all shadow-inner" :class="{'border-red-500/50': !audio.AudioSelectorName || audio.AudioSelectorName === 'None'}" @input="updateJson" />
                </div>
                 <div class="space-y-1.5 focus-within:z-20">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Stream Name</label>
                  <input v-model="audio.StreamName" type="text" placeholder="Undefined" class="w-full bg-black/20 border border-[#2a333d] rounded-sm px-4 py-2.5 text-sm text-[#e5eefc] focus:border-[#ffaa00]/30 outline-none transition-all shadow-inner" @input="updateJson" />
                </div>
                <div class="space-y-1.5 focus-within:z-20">
                  <label class="text-[10px] text-[#ffaa00] uppercase font-black tracking-widest ml-1 flex items-center gap-2">
                    <i class="bi bi-cpu"></i> Codec Settings <span class="text-red-500">*</span>
                  </label>
                  <select v-model="audio.codecType"
                          class="w-full bg-black/20 border border-[#ffaa00]/30 rounded-sm px-4 py-2.5 text-sm text-[#ffaa00] font-bold focus:outline-none cursor-pointer hover:border-[#ffaa00] transition-all appearance-none ring-1 ring-[#ffaa00]/10 shadow-lg shadow-[#ffaa00]/20"
                          :class="{'border-red-500/50': !audio.codecType || audio.codecType === 'None' || audio.codecType === 'NONE'}"
                          @change="onAudioCodecTypeChange(audio)">
                    <option :value="null">None</option>
                    <option value="AacSettings">AAC (Advanced Audio Coding)</option>
                    <option v-if="config.outputGroup.type !== 'RTMP'" value="Ac3Settings">AC3 (Dolby Digital)</option>
                    <option v-if="config.outputGroup.type !== 'RTMP'" value="Eac3Settings">EAC3 (Dolby Digital Plus)</option>
                    <option v-if="config.outputGroup.type !== 'RTMP'" value="Eac3AtmosSettings">EAC3 Atmos</option>
                    <option v-if="config.outputGroup.type !== 'HLS' && config.outputGroup.type !== 'RTMP'" value="Mp2Settings">MP2 (MPEG Audio Layer II)</option>
                    <option v-if="config.outputGroup.type !== 'RTMP'" value="PassThroughSettings">Pass Through</option>
                    <option v-if="config.outputGroup.type !== 'HLS' && config.outputGroup.type !== 'RTMP'" value="WavSettings">WAV (Uncompressed)</option>
                  </select>
                </div>
              </div>



              <!-- Additional Settings Row -->
              <div class="grid grid-cols-4 gap-6 mb-6">
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Language Code Control</label>
                  <select v-model="audio.LanguageCodeControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                    <option value="FOLLOW_INPUT">FOLLOW_INPUT</option>
                    <option value="USE_CONFIGURED">USE_CONFIGURED</option>
                  </select>
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Language Code</label>
                  <input v-model="audio.LanguageCode" type="text" placeholder="Undefined" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Audio Type Control</label>
                  <select v-model="audio.AudioTypeControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                    <option value="FOLLOW_INPUT">FOLLOW_INPUT</option>
                    <option value="USE_CONFIGURED">USE_CONFIGURED</option>
                  </select>
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Audio Type</label>
                  <select v-model="audio.AudioType" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                    <option :value="undefined">None</option>
                    <option value="CLEAN_EFFECTS">CLEAN_EFFECTS</option>
                    <option value="HEARING_IMPAIRED">HEARING_IMPAIRED</option>
                    <option value="VISUALLY_IMPAIRED_COMMENTARY">VISUALLY_IMPAIRED_COMMENTARY</option>
                    <option value="UNDEFINED">UNDEFINED</option>
                  </select>
                </div>
              </div>

              <!-- DASH Roles & Accessibility Row -->
              <div class="grid grid-cols-2 gap-6 mb-6">
                <div class="bg-black/20 p-4 rounded-sm border border-[#2a333d]">
                   <div class="flex items-center justify-between mb-4">
                      <div class="flex items-center gap-2">
                        <i class="bi bi-list-task text-[#ffaa00]"></i>
                        <span class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest">Audio DASH Roles</span>
                      </div>
                      <button type="button" @click="audio.AudioDashRoles.push('ALTERNATE'); updateJson();"  class="text-xs bg-[#ffaa00] hover:bg-[#ffaa00]/80 text-black px-1.5 py-0.5 rounded inline-flex items-center transition-all font-black uppercase tracking-tight active:scale-95 shadow-sm leading-none border border-[#ffaa00]/30">
                        <ma-plus class="mr-1 text-black! w-2 h-2"></ma-plus> <span class="text-black">Add</span>
                      </button>
                   </div>
                   <div class="flex flex-wrap gap-2">
                      <div v-for="(role, rIdx) in audio.AudioDashRoles" :key="'dash-role-' + rIdx" class="flex items-center bg-black/20 border border-[#2a333d] rounded overflow-hidden h-7 group/role transition-all hover:border-[#ffaa00]/30">
                        <select v-model="audio.AudioDashRoles[rIdx]" class="bg-transparent text-[9px] text-[#e5eefc] focus:outline-none cursor-pointer pl-2 pr-1 h-full border-r border-[#2a333d] font-bold" @change="updateJson();">
                          <option value="ALTERNATE">ALTERNATE</option>
                          <option value="COMMENTARY">COMMENTARY</option>
                          <option value="DESCRIPTION">DESCRIPTION</option>
                          <option value="DUB">DUB</option>
                          <option value="EMERGENCY">EMERGENCY</option>
                          <option value="ENHANCED-AUDIO-INTELLIGIBILITY">ENHANCED-AUDIO-INTELLIGIBILITY</option>
                          <option value="KARAOKE">KARAOKE</option>
                          <option value="MAIN">MAIN</option>
                          <option value="SUPPLEMENTARY">SUPPLEMENTARY</option>
                        </select>
                        <button type="button" @click="removeDashRole(audio, rIdx)"  class="text-white hover:bg-red-500 font-bold px-2 h-full flex items-center justify-center bg-red-600/80 cursor-pointer transition-colors duration-200 border-l border-red-500/20" title="Remove Role">
                          <span class="text-[12px] leading-none text-white">×</span>
                        </button>
                      </div>
                      <div v-if="audio.AudioDashRoles.length === 0" class="text-[9px] text-[#cdd6ee] italic uppercase font-black py-2">No roles added</div>
                   </div>
                </div>
                <div class="bg-black/20 p-4 rounded-sm border border-[#2a333d]">
                  <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest mb-4 flex items-center gap-2 leading-none">
                    <i class="bi bi-universal-access text-[#ffaa00]"></i> DVB DASH Accessibility
                  </div>
                  <select v-model="audio.DvbDashAccessibility" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-[10px] text-[#e5eefc] outline-none" @change="updateJson">
                    <option :value="undefined">None</option>
                    <option value="DVBDASH_1_VISUALLY_IMPAIRED">DVBDASH_1_VISUALLY_IMPAIRED</option>
                    <option value="DVBDASH_2_HARD_OF_HEARING">DVBDASH_2_HARD_OF_HEARING</option>
                    <option value="DVBDASH_3_SUPPLEMENTAL_COMMENTARY">DVBDASH_3_SUPPLEMENTAL_COMMENTARY</option>
                    <option value="DVBDASH_4_DIRECTORS_COMMENTARY">DVBDASH_4_DIRECTORS_COMMENTARY</option>
                    <option value="DVBDASH_5_EDUCATIONAL_NOTES">DVBDASH_5_EDUCATIONAL_NOTES</option>
                    <option value="DVBDASH_6_MAIN_PROGRAM">DVBDASH_6_MAIN_PROGRAM</option>
                    <option value="DVBDASH_7_CLEAN_FEED">DVBDASH_7_CLEAN_FEED</option>
                  </select>
                </div>
              </div>

              <!-- Audio Normalization & Remix Sub-Section -->
              <div class="grid grid-cols-2 gap-6 mb-6">
                <!-- Remix Settings -->
                <div class="bg-black/20 p-4 rounded-sm border border-[#2a333d]">
                  <div class="flex items-center justify-between mb-4">
                    <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-2 leading-none">
                       <i class="bi bi-sliders text-[#ffaa00]"></i> Remix Settings
                    </div>
                      <button type="button" @click="toggleRemixSettings(audio)"
                              class="text-xs font-black uppercase tracking-tight px-1.5 py-0.5 rounded inline-flex items-center border transition-all shadow-sm active:scale-95 text-black leading-none"
                              :class="audio.RemixSettings ? 'bg-red-600 border-red-500/40 hover:bg-red-500' : 'bg-[#ffaa00] border-[#ffaa00]/30 hover:bg-[#ffaa00]/80'">
                        <span class="text-black">{{ audio.RemixSettings ? 'Rem' : 'Add' }}</span>
                      </button>
                    </div>
                    <div v-if="audio.RemixSettings" class="space-y-4 animate-in slide-in-from-top-2">
                    <div class="grid grid-cols-2 gap-4">
                      <div class="space-y-1.5">
                        <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Input Channels</label>
                        <input v-model.number="audio.RemixSettings.ChannelsIn" type="number" placeholder="16" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc] outline-none" @input="updateJson" />
                      </div>
                      <div class="space-y-1.5">
                        <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Output Channels</label>
                        <input v-model.number="audio.RemixSettings.ChannelsOut" type="number" placeholder="2" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc] outline-none" @input="updateJson" />
                      </div>
                    </div>
                      <div class="space-y-3">
                      <div class="flex items-center justify-between leading-none">
                         <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1 tracking-widest leading-none">Channel Mappings</label>
                         <button type="button" @click="addRemixMapping(audio)"  class="text-[7px] bg-[#ffaa00] hover:bg-[#ffaa00]/80 text-white px-1.5 py-0.5 rounded border border-[#ffaa00]/30 transition-all font-black uppercase tracking-tight active:scale-95 shadow-sm leading-none">
                            <ma-plus class="bly-plus mr-1 text-white scale-75"></ma-plus> <span class="text-white">Add</span>
                         </button>
                      </div>
                      <div v-for="(map, mIdx) in audio.RemixSettings.ChannelMappings" :key="mIdx" class="bg-black/20 border border-[#2a333d] p-2.5 rounded-lg relative group/map hover:border-[#ffaa00]/30 transition-all mb-4">
                        <div class="flex items-center justify-between mb-4 pb-2 border-b border-[#2a333d]">
                           <div class="flex items-center gap-3">
                             <span class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest leading-none">Output Ch</span>
                             <input v-model.number="map.OutputChannel" type="number"
                                    class="bg-black/20 border border-[#2a333d] rounded px-3 py-1.5 text-sm text-[#ffaa00] font-bold outline-none focus:border-[#ffaa00]/30 transition-colors shadow-inner min-w-[80px]"
                                    placeholder="0"
                                    @input="updateJson" />
                           </div>
                           <button type="button" @click="removeRemixMapping(audio, mIdx)"
                                   class="size-7 flex items-center justify-center bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white rounded-lg border border-red-500/20 transition-all active:scale-90"
                                   title="Delete Mapping">
                             <ma-delete class="text-sm ma-btn"></ma-delete>
                           </button>
                        </div>

                        <div class="pl-4 border-l-2 border-[#2a333d] overflow-hidden min-h-[60px] space-y-4">
                           <div class="flex items-center justify-between">
                              <label class="text-[9px] text-[#cdd6ee] uppercase font-black tracking-widest leading-none">Input Gains (dB)</label>
                              <div class="flex items-center gap-2">
                                <button type="button" @click="map.InputChannelLevels.push({ InputChannel: 0, Gain: 0 }); updateJson()"
                                        class="px-1.5 py-0.5 bg-[#ffaa00] hover:bg-[#ffaa00]/80 text-xs text-white font-black rounded uppercase transition-all active:scale-95 leading-none shadow-sm flex items-center gap-1">
                                   <ma-plus class="bly-plus scale-75"></ma-plus> <span class="text-white">Add</span>
                                </button>
                                <button type="button" v-if="(map.InputChannelLevels || []).length" @click="map.InputChannelLevels.pop(); updateJson()"
                                        class="px-1.5 py-0.5 bg-red-600 hover:bg-red-500 text-xs text-white font-black rounded uppercase transition-all active:scale-95 leading-none shadow-sm flex items-center gap-1">
                                   <i class="bi bi-dash-lg scale-75"></i> <span class="text-white">Del</span>
                                </button>
                              </div>
                           </div>
                           <div class="flex flex-wrap gap-2 pb-1">
                              <div v-for="(lvl, lIdx) in (map.InputChannelLevels || [])" :key="lIdx" class="flex items-center gap-1.5 bg-black/20 border border-[#2a333d] rounded px-1.5 py-1 group/lvl hover:border-[#ffaa00]/30 transition-all">
                                <div class="flex flex-col">
                                  <span class="text-[5px] text-[#cdd6ee] uppercase font-black scale-90 -mb-0.5 opacity-60">In</span>
                                  <input v-model.number="lvl.InputChannel" type="number" title="Input Ch" class="w-6 bg-transparent text-[9px] text-[#ffaa00] font-bold focus:outline-none" @input="updateJson" />
                                </div>
                                <div class="w-px h-4 bg-black/20 mx-0.5"></div>
                                <div class="flex flex-col">
                                  <span class="text-[5px] text-[#cdd6ee] uppercase font-black scale-90 -mb-0.5 opacity-60 text-right">dB</span>
                                  <input v-model.number="lvl.Gain" type="number" step="0.1" title="Gain" class="w-8 bg-transparent text-[9px] text-emerald-400 font-bold focus:outline-none text-right" @input="updateJson" />
                                </div>
                                <button type="button" @click="removeInputLevel(map, lIdx)"  class="ml-1 px-1 py-1 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all shadow-md shadow-red-900/40 active:scale-90 flex items-center justify-center size-4" title="Remove Individual Gain">
                                  <i class="bi bi-x text-[10px] font-black leading-none text-white"></i>
                                </button>
                              </div>
                              <div v-if="!(map.InputChannelLevels || []).length" class="text-[7px] text-[#94a3b8] italic font-black uppercase tracking-tight py-1">Muted (No gains)</div>
                           </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="py-2 text-center border border-dashed border-[#2a333d] rounded-sm">
                    <p class="text-[9px] text-[#cdd6ee] font-bold uppercase tracking-widest italic">Remixing disabled (default None)</p>
                  </div>
                </div>

                <!-- Normalization Settings -->
                <div class="bg-black/20 p-4 rounded-sm border border-[#2a333d]">
                  <div class="flex items-center justify-between mb-4">
                    <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-2 leading-none">
                       <i class="bi bi-waveform text-[#ffaa00]"></i> Normalization
                    </div>
                    <button type="button"
                      @click="toggleNormalizationSettings(audio)"

                      class="text-xs font-black uppercase tracking-tight px-1.5 py-0.5 rounded inline-flex border transition-all shadow-sm active:scale-95 leading-none"
                      :class="audio.AudioNormalizationSettings ? 'bg-red-600 border-red-500/40 text-black hover:bg-red-500' : 'bg-[#ffaa00] border-[#ffaa00]/30 text-black hover:bg-[#ffaa00]/80'"
                    >
                      <span class="text-black">{{ audio.AudioNormalizationSettings ? 'Rem' : 'Add' }}</span>
                    </button>
                  </div>
                  <div v-if="audio.AudioNormalizationSettings" class="grid grid-cols-1 gap-4 animate-in slide-in-from-top-2">
                    <div class="grid grid-cols-2 gap-4">
                      <div class="space-y-1.5">
                        <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Algorithm</label>
                        <select v-model="audio.AudioNormalizationSettings.Algorithm" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc] outline-none" @change="updateJson">
                          <option value="ITU_1770_1">ITU_1770_1</option>
                          <option value="ITU_1770_2">ITU_1770_2</option>
                        </select>
                      </div>
                      <div class="space-y-1.5">
                        <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Algorithm Control</label>
                        <select v-model="audio.AudioNormalizationSettings.AlgorithmControl" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc] outline-none" @change="updateJson">
                           <option value="CORRECT_AUDIO">CORRECT_AUDIO</option>
                        </select>
                      </div>
                    </div>
                    <div class="space-y-1.5">
                        <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Target LKFS</label>
                        <input v-model.number="audio.AudioNormalizationSettings.TargetLkfs" type="number" step="0.1" placeholder="Undefined" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc] outline-none" @input="updateJson" />
                    </div>
                  </div>
                  <div v-else class="py-2 text-center border border-dashed border-[#2a333d] rounded-sm">
                    <p class="text-[9px] text-[#cdd6ee] font-bold uppercase tracking-widest italic">Normalization disabled (default None)</p>
                  </div>
                </div>
              </div>

              <!-- Watermarks Row -->
              <div class="bg-black/20 p-4 rounded-sm border border-[#2a333d] mb-6">
                 <div class="flex items-center justify-between mb-4">
                    <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-2 leading-none">
                       <i class="bi bi-droplet text-[#ffaa00]"></i> Audio Watermark Settings
                    </div>
                    <select v-model="audio.activeWatermark" class="text-xs bg-black/20 border border-[#2a333d] rounded px-2 py-0.5 text-[#cdd6ee]" @change="onWatermarkChange(audio)">
                      <option :value="null">None</option>
                      <option value="NielsenWatermarksSettings">Nielsen Watermarks</option>
                    </select>
                 </div>
                 <div v-if="audio.activeWatermark === 'NielsenWatermarksSettings' && audio.AudioWatermarkSettings && audio.AudioWatermarkSettings.NielsenWatermarksSettings" class="grid grid-cols-2 gap-6 animate-in slide-in-from-top-2">
                    <div class="space-y-1.5">
                      <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Distribution Type</label>
                      <select v-model="audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenDistributionType" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#e5eefc] outline-none" @change="updateJson">
                        <option value="PROGRAM_CONTENT">PROGRAM_CONTENT</option>
                        <option value="FINAL_DISTRIBUTION">FINAL_DISTRIBUTION</option>
                      </select>
                    </div>

                    <!-- CBET Settings -->
                    <div class="space-y-4 border border-[#2a333d] p-3 rounded-sm bg-black/20">
                      <div class="flex items-center justify-between leading-none">
                        <span class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest">CBET Settings</span>
                        <button type="button"
                          @click="toggleCbet(audio)"
                          class="text-[7px] px-1.5 py-0.5 rounded inline-flex border transition-all font-black uppercase tracking-tight shadow-sm active:scale-95 text-white leading-none"
                          :class="audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenCbetSettings ? 'bg-red-600 border-red-500/40 hover:bg-red-500' : 'bg-[#ffaa00] border-[#ffaa00]/30 hover:bg-[#ffaa00]/80'"
                        >
                          {{ audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenCbetSettings ? 'Rem' : 'Add' }}
                        </button>
                      </div>

                      <div v-if="audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenCbetSettings" class="grid grid-cols-1 gap-3 animate-in fade-in duration-200">
                        <div class="space-y-1">
                          <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">CSID</label>
                          <input v-model="audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenCbetSettings.Csid" type="text" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-xs text-[#e5eefc] outline-none" @input="updateJson" />
                        </div>
                        <div class="space-y-1">
                          <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Check Digit String</label>
                          <input v-model="audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenCbetSettings.CbetCheckDigitString" type="text" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-xs text-[#e5eefc] outline-none" @input="updateJson" />
                        </div>
                        <div class="space-y-1">
                          <label class="text-xs text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Stepaside</label>
                          <select v-model="audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenCbetSettings.CbetStepaside" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-xs text-[#e5eefc] outline-none" @change="updateJson">
                            <option value="DISABLED">DISABLED</option>
                            <option value="ENABLED">ENABLED</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <!-- NAES II and NW Settings -->
                    <div class="space-y-4 border border-[#2a333d] p-3 rounded-sm bg-black/20">
                      <div class="flex items-center justify-between">
                        <span class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest">NAES II / NW Setting</span>
                        <button type="button"
                          v-if="!(audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenNaesIiNwSettings || []).length"
                          @click="addNaesIiNw(audio)"
                          class="bg-[#ffaa00] border border-[#ffaa00]/30 text-white text-[7px] px-1.5 py-1 rounded transition-all font-black uppercase tracking-widest shadow-sm active:scale-95"
                        >
                          Add SID
                        </button>
                      </div>

                      <div v-for="(naes, nIdx) in (audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenNaesIiNwSettings || [])" :key="nIdx"
                           class="bg-black/20 border border-[#2a333d] rounded-lg p-3 space-y-3 relative group-item/naes animate-in slide-in-from-right-2">
                        <button type="button" @click="removeNaesIiNw(audio, nIdx)"  class="absolute top-2 right-2 size-5 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full transition-all shadow-lg shadow-red-900/40 active:scale-95 border border-red-500/20" title="Remove SID">
                          <ma-close class="bly-close text-[10px] text-white"></ma-close>
                        </button>
                        <div class="grid grid-cols-2 gap-2">
                          <div class="space-y-1">
                            <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">SID</label>
                            <input v-model.number="naes.Sid" type="number" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc]" @input="updateJson" />
                          </div>
                          <div class="space-y-1">
                            <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Check Digit</label>
                            <input v-model="naes.CheckDigitString" type="text" maxlength="1" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc] text-center" @input="updateJson" />
                          </div>
                        </div>
                        <div class="space-y-1">
                          <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Timezone</label>
                          <select v-model="naes.Timezone" class="w-full bg-black/20 border border-[#2a333d] rounded px-2 py-1 text-[10px] text-[#e5eefc]" @change="updateJson">
                            <option v-for="tz in getNielsenTimezones()" :key="tz" :value="tz">{{ tz }}</option>
                          </select>
                        </div>
                      </div>
                      <div v-if="!(audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenNaesIiNwSettings || []).length" class="text-xs text-[#cdd6ee] italic text-center py-2">No NAES II/NW configurations</div>
                    </div>
                 </div>
                 <div v-else class="py-2 text-center border border-dashed border-[#2a333d] rounded-sm">
                    <p class="text-[9px] text-[#cdd6ee] font-bold uppercase tracking-widest italic">Watermarks disabled (None)</p>
                 </div>
              </div>

              <!-- Audio Codec Settings TABS -->
              <div v-if="audio.codecType && audio.codecType !== 'PassThroughSettings'" class="space-y-4">
                <div class="flex gap-1 border-b border-[#2a333d] overflow-x-auto no-scrollbar scroll-smooth">
                  <button type="button"
                    v-for="aTab in getAudioCodecTabs(audio)"
                    :key="aTab.id"

                    class="px-3 py-2 text-[9px] font-black uppercase tracking-widest transition-all duration-200 whitespace-nowrap min-w-fit flex-shrink-0"
                    :class="(audio.activeCodecTab || 'config') === aTab.id ? 'text-[#ffaa00] border-b-2 border-[#ffaa00] bg-[#ffaa00]/15' : 'text-[#cdd6ee] hover:text-[#cdd6ee] hover:bg-black/20'"
                    @click.stop="setAudioTab(audio, aTab.id)"
                  >
                    {{ aTab.label }}
                  </button>
                </div>

                <div class="bg-black/20 p-5 rounded-sm border border-[#2a333d] min-h-[160px]">
                  <!-- AAC Configuration Tab -->
                  <div v-if="audio.codecType === 'AacSettings' && (audio.activeCodecTab || 'config') === 'config'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Coding Mode</label>
                      <select v-model="audio.CodecSettings.AacSettings.CodingMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="AD_RECEIVER_MIX">AD_RECEIVER_MIX</option>
                        <option value="CODING_MODE_1_0">CODING_MODE_1_0</option>
                        <option value="CODING_MODE_1_1">CODING_MODE_1_1</option>
                        <option value="CODING_MODE_2_0">CODING_MODE_2_0</option>
                        <option value="CODING_MODE_5_1">CODING_MODE_5_1</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Profile</label>
                      <select v-model="audio.CodecSettings.AacSettings.Profile" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="HEV1">HEV1</option>
                        <option value="HEV2">HEV2</option>
                        <option value="LC">LC</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitrate</label>
                      <input v-model.number="audio.CodecSettings.AacSettings.Bitrate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Sample Rate</label>
                      <input v-model.number="audio.CodecSettings.AacSettings.SampleRate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Input Type</label>
                      <select v-model="audio.CodecSettings.AacSettings.InputType" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NORMAL">NORMAL</option>
                        <option value="BROADCASTER_MIXED_AD">BROADCASTER_MIXED_AD</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Raw Format</label>
                      <select v-model="audio.CodecSettings.AacSettings.RawFormat" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="LATM_LOAS">LATM_LOAS</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Spec</label>
                      <select v-model="audio.CodecSettings.AacSettings.Spec" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="MPEG2">MPEG2</option>
                        <option value="MPEG4">MPEG4</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Rate Control Mode</label>
                      <select v-model="audio.CodecSettings.AacSettings.RateControlMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="CBR">CBR</option>
                        <option value="VBR">VBR</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">VBR Quality</label>
                      <select v-model="audio.CodecSettings.AacSettings.VbrQuality" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option :value="undefined">Undefined</option>
                        <option value="LOW">LOW</option>
                        <option value="MEDIUM_LOW">MEDIUM_LOW</option>
                        <option value="MEDIUM_HIGH">MEDIUM_HIGH</option>
                        <option value="HIGH">HIGH</option>
                      </select>
                    </div>
                  </div>

                  <!-- AC3 Configuration Tab -->
                  <div v-if="audio.codecType === 'Ac3Settings' && (audio.activeCodecTab || 'config') === 'config'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Metadata Control</label>
                      <select v-model="audio.CodecSettings.Ac3Settings.MetadataControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="FOLLOW_INPUT">FOLLOW_INPUT</option>
                        <option value="USE_CONFIGURED">USE_CONFIGURED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Coding Mode</label>
                      <select v-model="audio.CodecSettings.Ac3Settings.CodingMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option v-for="mode in getDolbyCodingModes('Ac3Settings')" :key="mode" :value="mode">{{ mode }}</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitstream Mode</label>
                      <select v-model="audio.CodecSettings.Ac3Settings.BitstreamMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="COMMENTARY">COMMENTARY</option>
                        <option value="COMPLETE_MAIN">COMPLETE_MAIN</option>
                        <option value="DIALOGUE">DIALOGUE</option>
                        <option value="EMERGENCY">EMERGENCY</option>
                        <option value="HEARING_IMPAIRED">HEARING_IMPAIRED</option>
                        <option value="MUSIC_AND_EFFECTS">MUSIC_AND_EFFECTS</option>
                        <option value="VISUALLY_IMPAIRED">VISUALLY_IMPAIRED</option>
                        <option value="VOICE_OVER">VOICE_OVER</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitrate</label>
                      <input v-model.number="audio.CodecSettings.Ac3Settings.Bitrate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                  </div>

                  <!-- AC3 Encoder Parameters Tab -->
                  <div v-if="audio.codecType === 'Ac3Settings' && audio.activeCodecTab === 'enc_params'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">LFE Filter</label>
                      <select v-model="audio.CodecSettings.Ac3Settings.LfeFilter" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="DISABLED">DISABLED</option>
                        <option value="ENABLED">ENABLED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Attenuation Control</label>
                      <select v-model="audio.CodecSettings.Ac3Settings.AttenuationControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="ATTENUATE_3_DB">ATTENUATE_3_DB</option>
                      </select>
                    </div>
                  </div>

                  <!-- AC3 Metadata Handling Tab -->
                  <div v-if="audio.codecType === 'Ac3Settings' && audio.activeCodecTab === 'meta_handling'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Dialnorm</label>
                      <input v-model.number="audio.CodecSettings.Ac3Settings.Dialnorm" type="number" step="1" placeholder="Undefined" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">DRC Profile</label>
                      <select v-model="audio.CodecSettings.Ac3Settings.DrcProfile" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="FILM_STANDARD">FILM_STANDARD</option>
                      </select>
                    </div>
                  </div>

                  <!-- EAC3 Configuration Tab -->
                  <div v-if="audio.codecType === 'Eac3Settings' && (audio.activeCodecTab || 'config') === 'config'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Passthrough Control</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.PassthroughControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NO_PASSTHROUGH">NO_PASSTHROUGH</option>
                        <option value="WHEN_POSSIBLE">WHEN_POSSIBLE</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Metadata Control</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.MetadataControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="FOLLOW_INPUT">FOLLOW_INPUT</option>
                        <option value="USE_CONFIGURED">USE_CONFIGURED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Coding Mode</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.CodingMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option v-for="mode in getDolbyCodingModes('Eac3Settings')" :key="mode" :value="mode">{{ mode }}</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitstream Mode</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.BitstreamMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="COMMENTARY">COMMENTARY</option>
                        <option value="COMPLETE_MAIN">COMPLETE_MAIN</option>
                        <option value="EMERGENCY">EMERGENCY</option>
                        <option value="HEARING_IMPAIRED">HEARING_IMPAIRED</option>
                        <option value="VISUALLY_IMPAIRED">VISUALLY_IMPAIRED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitrate</label>
                      <input v-model.number="audio.CodecSettings.Eac3Settings.Bitrate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                  </div>

                  <!-- EAC3 Encoder Parameters Tab -->
                  <div v-if="audio.codecType === 'Eac3Settings' && audio.activeCodecTab === 'enc_params'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">LFE Filter</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.LfeFilter" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="DISABLED">DISABLED</option>
                        <option value="ENABLED">ENABLED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Phase Control</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.PhaseControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NO_SHIFT">NO_SHIFT</option>
                        <option value="SHIFT_90_DEGREES">SHIFT_90_DEGREES</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Attenuation Control</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.AttenuationControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="ATTENUATE_3_DB">ATTENUATE_3_DB</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">DC Filter</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.DcFilter" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="DISABLED">DISABLED</option>
                        <option value="ENABLED">ENABLED</option>
                      </select>
                    </div>
                  </div>

                  <!-- EAC3 Metadata Handling Tab -->
                  <div v-if="audio.codecType === 'Eac3Settings' && audio.activeCodecTab === 'meta_handling'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Dialnorm</label>
                      <input v-model.number="audio.CodecSettings.Eac3Settings.Dialnorm" type="number" step="1" placeholder="Undefined" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">DRC Line</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.DrcLine" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="FILM_LIGHT">FILM_LIGHT</option>
                        <option value="FILM_STANDARD">FILM_STANDARD</option>
                        <option value="MUSIC_LIGHT">MUSIC_LIGHT</option>
                        <option value="MUSIC_STANDARD">MUSIC_STANDARD</option>
                        <option value="SPEECH">SPEECH</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">DRC RF</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.DrcRf" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="FILM_LIGHT">FILM_LIGHT</option>
                        <option value="FILM_STANDARD">FILM_STANDARD</option>
                        <option value="MUSIC_LIGHT">MUSIC_LIGHT</option>
                        <option value="MUSIC_STANDARD">MUSIC_STANDARD</option>
                        <option value="SPEECH">SPEECH</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Surround Extra Mode</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.SurroundExMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="DISABLED">DISABLED</option>
                        <option value="ENABLED">ENABLED</option>
                        <option value="NOT_INDICATED">NOT_INDICATED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Surround Mode</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.SurroundMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="DISABLED">DISABLED</option>
                        <option value="ENABLED">ENABLED</option>
                        <option value="NOT_INDICATED">NOT_INDICATED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Stereo Downmix</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.StereoDownmix" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="DPL2">DPL2</option>
                        <option value="LO_RO">LO_RO</option>
                        <option value="LT_RT">LT_RT</option>
                        <option value="NOT_INDICATED">NOT_INDICATED</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Lo/Ro Center Level</label>
                      <input v-model.number="audio.CodecSettings.Eac3Settings.LoRoCenterMixLevel" type="number" step="0.5" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Lo/Ro Surround Level</label>
                      <input v-model.number="audio.CodecSettings.Eac3Settings.LoRoSurroundMixLevel" type="number" step="0.5" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Lt/Rt Center Level</label>
                      <input v-model.number="audio.CodecSettings.Eac3Settings.LtRtCenterMixLevel" type="number" step="0.5" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Lt/Rt Surround Level</label>
                      <input v-model.number="audio.CodecSettings.Eac3Settings.LtRtSurroundMixLevel" type="number" step="0.5" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">LFE Control</label>
                      <select v-model="audio.CodecSettings.Eac3Settings.LfeControl" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="LFE">LFE</option>
                        <option value="NO_LFE">NO_LFE</option>
                      </select>
                    </div>
                  </div>

                  <!-- Atmos Configuration Tab -->
                  <div v-if="audio.codecType === 'Eac3AtmosSettings' && (audio.activeCodecTab || 'config') === 'config'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitrate</label>
                      <input v-model.number="audio.CodecSettings.Eac3AtmosSettings.Bitrate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Coding Mode</label>
                      <select v-model="audio.CodecSettings.Eac3AtmosSettings.CodingMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option v-for="mode in getDolbyCodingModes('Eac3AtmosSettings')" :key="mode" :value="mode">{{ mode }}</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Surround Trim</label>
                      <input v-model.number="audio.CodecSettings.Eac3AtmosSettings.SurroundTrim" type="number" step="0.5" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Height Trim</label>
                      <input v-model.number="audio.CodecSettings.Eac3AtmosSettings.HeightTrim" type="number" step="0.5" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                  </div>

                  <!-- Atmos Metadata Handling Tab -->
                  <div v-if="audio.codecType === 'Eac3AtmosSettings' && audio.activeCodecTab === 'meta_handling'" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Dialnorm</label>
                      <input v-model.number="audio.CodecSettings.Eac3AtmosSettings.Dialnorm" type="number" step="1" placeholder="Undefined" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">DRC Line</label>
                      <select v-model="audio.CodecSettings.Eac3AtmosSettings.DrcLine" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="FILM_LIGHT">FILM_LIGHT</option>
                        <option value="FILM_STANDARD">FILM_STANDARD</option>
                        <option value="MUSIC_LIGHT">MUSIC_LIGHT</option>
                        <option value="MUSIC_STANDARD">MUSIC_STANDARD</option>
                        <option value="SPEECH">SPEECH</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">DRC RF</label>
                      <select v-model="audio.CodecSettings.Eac3AtmosSettings.DrcRf" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option value="NONE">NONE</option>
                        <option value="FILM_LIGHT">FILM_LIGHT</option>
                        <option value="FILM_STANDARD">FILM_STANDARD</option>
                        <option value="MUSIC_LIGHT">MUSIC_LIGHT</option>
                        <option value="MUSIC_STANDARD">MUSIC_STANDARD</option>
                        <option value="SPEECH">SPEECH</option>
                      </select>
                    </div>
                  </div>

                  <!-- MP2/WAV Configuration Tab -->
                  <div v-if="['Mp2Settings', 'WavSettings'].includes(audio.codecType)" class="grid grid-cols-3 gap-6 animate-in fade-in duration-200">
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Coding Mode</label>
                      <select v-model="audio.CodecSettings[audio.codecType].CodingMode" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option v-for="mode in (audio.codecType === 'WavSettings' ? ['CODING_MODE_1_0', 'CODING_MODE_2_0', 'CODING_MODE_4_0', 'CODING_MODE_8_0'] : ['CODING_MODE_1_0', 'CODING_MODE_2_0'])" :key="mode" :value="mode">{{ mode }}</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bitrate</label>
                      <input v-if="audio.codecType === 'Mp2Settings'" v-model.number="audio.CodecSettings.Mp2Settings.Bitrate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                      <div v-else class="text-[10px] text-[#cdd6ee] italic py-2">N/A for WAV</div>
                    </div>
                    <div class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Sample Rate</label>
                      <input v-model.number="audio.CodecSettings[audio.codecType].SampleRate" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                    </div>
                    <div v-if="audio.codecType === 'WavSettings'" class="space-y-1.5">
                      <label class="block text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest">Bit Depth</label>
                      <select v-model="audio.CodecSettings.WavSettings.BitDepth" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @change="updateJson">
                        <option :value="16">16</option>
                        <option :value="24">24</option>
                        <option :value="32">32</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section: Individual Outputs -->
      <section class="space-y-4">
        <div class="flex items-center justify-between px-1">
          <div class="flex items-center gap-3">

            <span class="text-[10px] font-black text-[#cdd6ee] uppercase tracking-widest">Individual Outputs</span>
            <span class="bg-[#ffaa00]/15 text-[#ffaa00] text-[10px] px-2 py-0.5 rounded-full font-black border border-[#ffaa00]/20">{{ config.outputGroup.outputs.length }}</span>
            <div class="h-px flex-1 bg-black/20"></div>
          </div>
          <button type="button"
            @click="addOutput"

            class="group flex items-center gap-1 text-xs bg-[#ffaa00]/80 hover:bg-[#ffaa00] text-black! font-black uppercase tracking-tighter px-1 py-0.5 rounded-sm transition-all active:scale-95 ml-4 whitespace-nowrap border border-[#ffaa00]/20"
          >
            <ma-plus class="bly-plus transition-transform text-black! text-[8px]"></ma-plus>
            <span class="tracking-wide">Add Variant</span>
          </button>
        </div>

        <div class="grid gap-6">
          <div v-for="(output, index) in config.outputGroup.outputs" :key="index"
               class="bg-black/15 rounded-xs overflow-hidden relative group/item  transition-all duration-500">

            <!-- Collapsible Header -->
            <div
              @click="toggleOutputMinimize(output, $event)"
              class="px-6 py-3 bg-black/20 flex items-center justify-between cursor-pointer hover:bg-black/20 transition-colors"
            >
              <div class="flex items-center gap-4">
                <!-- <i class="bi" :class="output.isMinimized ? 'bi-chevron-right' : 'bi-chevron-down'"></i> -->
                <ma-chevron-down class="w-3 h-3" :class="output.isMinimized ? 'rotate-0' : 'rotate-180'"></ma-chevron-down>
                <div class="flex items-center gap-3">
                  <span class="text-xs font-bold text-white">{{ output.OutputName || 'Unnamed Output' }}</span>
                  <span v-if="config.outputGroup.type === 'HLS'" class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest bg-black/20 px-2 py-0.5 rounded border border-[#2a333d]">{{ output.OutputSettings.HlsOutputSettings.HlsSettingsType.replace('HlsSettings', '') }}</span>
                  <span v-else class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest bg-black/20 px-2 py-0.5 rounded border border-[#2a333d]">ARCHIVE</span>
                  <span v-if="config.outputGroup.type === 'HLS' && output.OutputSettings.HlsOutputSettings.NameModifier" class="text-[10px] text-[#ffaa00] font-mono bg-[#ffaa00]/15 px-2 py-0.5 rounded border border-[#ffaa00]/20">{{ output.OutputSettings.HlsOutputSettings.NameModifier }}</span>
                  <span v-if="config.outputGroup.type === 'ARCHIVE' && output.OutputSettings.ArchiveOutputSettings.NameModifier" class="text-[10px] text-[#ffaa00] font-mono bg-[#ffaa00]/15 px-2 py-0.5 rounded border border-[#ffaa00]/20">{{ output.OutputSettings.ArchiveOutputSettings.NameModifier }}</span>
                </div>
              </div>
              <button type="button"
                @click.stop="removeOutput(index)"
                class="size-5 flex items-center justify-center  hover:text-red-600 text-white rounded-full transition-all shadow-lg active:scale-90"
                title="Delete Output"
              >
                <ma-delete class="text-sm ma-btn"></ma-delete>
              </button>
            </div>

            <div v-show="!output.isMinimized" class="p-6">
              <div class="grid grid-cols-2 gap-x-6 gap-y-4 mb-6">
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Output Name <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="output.OutputName"
                    type="text"
                    placeholder="e.g. HD-HLS-Output"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    :class="{'border-red-500/50': !output.OutputName || output.OutputName === 'None'}"
                    @input="updateJson"
                  />
                </div>

                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Video Description Name <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="output.VideoDescriptionName"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    :class="{'border-red-500/50': !output.VideoDescriptionName || output.VideoDescriptionName === 'None'}"
                    @change="updateJson"
                  >
                    <option value="">(None)</option>
                    <option v-for="video in config.videoDescriptions" :key="video.Name" :value="video.Name">
                      {{ video.Name }}
                    </option>
                  </select>
                </div>

                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Audio Description Names <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="output.AudioDescriptionNames"
                    multiple
                    class="w-full bg-black/20 border border-[#2a333d] rounded-sm px-4 py-2 text-xs text-[#ffaa00] font-mono focus:outline-none focus:border-[#ffaa00]/30 cursor-pointer min-h-[80px]"
                    :class="{'border-red-500/50': !output.AudioDescriptionNames || output.AudioDescriptionNames.length === 0 || (output.AudioDescriptionNames.length === 1 && output.AudioDescriptionNames[0] === 'None')}"
                    @change="updateJson"
                  >
                    <option v-for="audio in config.audioDescriptions" :key="audio.Name" :value="audio.Name">
                      {{ audio.Name }} ({{ audio.AudioSelectorName }})
                    </option>
                  </select>
                  <p class="text-xs text-[#cdd6ee] mt-1 italic leading-tight">Ctrl/Cmd+Click to select multiple (Required: At least one)</p>
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'HLS'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Name Modifier <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="output.OutputSettings.HlsOutputSettings.NameModifier"
                    type="text"
                    placeholder="e.g. _hd"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    :class="{'border-red-500/50': !output.OutputSettings.HlsOutputSettings.NameModifier || output.OutputSettings.HlsOutputSettings.NameModifier === 'None'}"
                    @input="updateJson"
                  />
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'HLS'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Segment Modifier</label>
                  <input
                    v-model="output.OutputSettings.HlsOutputSettings.SegmentModifier"
                    type="text"
                    placeholder="e.g. $current_time$"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @input="updateJson"
                  />
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'HLS'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Hls Settings Type</label>
                  <select
                    v-model="output.OutputSettings.HlsOutputSettings.HlsSettingsType"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @change="onHlsSettingsTypeChange(output)"
                  >
                    <option value="StandardHlsSettings">Standard HLS</option>
                    <option value="Fmp4HlsSettings">fMP4 HLS</option>
                    <option value="AudioOnlyHlsSettings">Audio Only HLS</option>
                    <option value="FrameCaptureHlsSettings">Frame Capture HLS</option>
                  </select>
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'HLS'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">H.265 Packaging Type</label>
                  <select
                    v-model="output.OutputSettings.HlsOutputSettings.H265PackagingType"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @change="updateJson"
                  >
                    <option value="HEV1">HEV1</option>
                    <option value="HVC1">HVC1</option>
                  </select>
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'RTMP'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Destination ID Ref <span class="text-red-500">*</span>
                  </label>
                  <select
                    v-model="output.OutputSettings.RtmpOutputSettings.Destination.DestinationRefId"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    :class="{'border-red-500/50': !output.OutputSettings.RtmpOutputSettings.Destination.DestinationRefId}"
                    @change="updateJson"
                  >
                    <option value="FROM_TEMPLATE">FROM_TEMPLATE</option>
                    <option v-for="dest in config.destinations" :key="dest.Id" :value="dest.Id">{{ dest.Id }}</option>
                  </select>
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'RTMP'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Certificate Mode</label>
                  <select
                    v-model="output.OutputSettings.RtmpOutputSettings.CertificateMode"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @change="updateJson"
                  >
                    <option value="SELF_SIGNED">SELF_SIGNED</option>
                    <option value="VERIFY_AUTHENTICITY">VERIFY_AUTHENTICITY</option>
                  </select>
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'RTMP'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Connection Retry Interval (sec) <i class="bi bi-info-circle ml-0.5" title="Number of seconds to wait before retrying a connection to the Flash Media server, after a disconnection."></i></label>
                  <input
                    v-model.number="output.OutputSettings.RtmpOutputSettings.ConnectionRetryInterval"
                    type="number"
                    min="0"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @input="updateJson"
                  />
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'RTMP'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Num Retries <i class="bi bi-info-circle ml-0.5" title="Number of retry attempts."></i></label>
                  <input
                    v-model.number="output.OutputSettings.RtmpOutputSettings.NumRetries"
                    type="number"
                    min="0"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @input="updateJson"
                  />
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'ARCHIVE'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">
                    Name Modifier <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="output.OutputSettings.ArchiveOutputSettings.NameModifier"
                    type="text"
                    placeholder="e.g. _archive"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    :class="{'border-red-500/50': !output.OutputSettings.ArchiveOutputSettings.NameModifier || output.OutputSettings.ArchiveOutputSettings.NameModifier === 'None'}"
                    @input="updateJson"
                  />
                </div>

                <div class="space-y-1.5" v-if="config.outputGroup.type === 'ARCHIVE'">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">File Extension</label>
                  <input
                    v-model="output.OutputSettings.ArchiveOutputSettings.Extension"
                    type="text"
                    placeholder="e.g. ts"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @input="updateJson"
                  />
                </div>
              </div> <!-- Close output header grid -->

            <!-- HLS-specific output settings panels -->
            <template v-if="config.outputGroup.type === 'HLS'">
            <!-- M3u8 Settings Panel -->
            <div v-if="output.OutputSettings.HlsOutputSettings.HlsSettingsType === 'StandardHlsSettings'" class="bg-black/20 p-4 rounded-sm border border-[#2a333d] space-y-4 mb-4">
              <div class="flex items-center justify-between border-b border-[#2a333d] pb-3 mb-2">
                <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-2">
                  <i class="bi bi-gear-fill text-[#ffaa00]"></i> HLS Settings (Standard)
                </div>
                <div class="flex items-center gap-2 bg-black/20 px-2 py-1 rounded-lg border border-[#2a333d]" title="M3U8 Settings">
                  <span class="text-xs text-[#cdd6ee] font-black uppercase">M3U8 Settings:</span>
                  <span class="text-[9px] text-[#ffaa00] font-black uppercase">M3U8</span>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-x-6 gap-y-4">
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Audio Rendition Set <i class="bi bi-info-circle ml-1" title="program_audio"></i></label>
                  <input
                    v-model="getHlsSettings(output).AudioRenditionSets"
                    type="text"
                    placeholder="program_audio"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @input="updateJson"
                  />
                  <p class="text-xs text-[#cdd6ee] mt-1 italic leading-tight">List all audio group IDs used with this video, separated by commas.</p>
                </div>
              </div>

              <!-- Tabs for Standard HLS Settings -->
              <div
                role="radiogroup"
                aria-label="Output details tabs"
                class="inline-flex flex-wrap gap-1 rounded-lg bg-black/70 p-1 overflow-x-auto no-scrollbar"
              >
                <button type="button"
                  role="radio"
                  :aria-checked="!output.activeSubTab || output.activeSubTab === 'config'"
                  class="rounded-sm px-2.5 py-1 text-xs font-medium tracking-[0.01em] transition-[background-color,color] duration-150 whitespace-nowrap"
                  :class="(!output.activeSubTab || output.activeSubTab === 'config') ? 'bg-[#ffaa00] text-black' : 'bg-transparent text-slate-300 hover:text-white'"
                  @click.stop="setSubTab(output, 'config')"
                >
                  Transport/Container Configuration
                </button>
                <button type="button"
                  role="radio"
                  :aria-checked="output.activeSubTab === 'pid'"
                  class="rounded-sm px-2.5 py-1 text-xs font-medium tracking-[0.01em] transition-[background-color,color] duration-150 whitespace-nowrap"
                  :class="output.activeSubTab === 'pid' ? 'bg-[#ffaa00] text-black' : 'bg-transparent text-slate-300 hover:text-white'"
                  @click.stop="setSubTab(output, 'pid')"
                >
                  PID Settings
                </button>
              </div>

              <!-- Transport/Container Configuration Tab -->
              <div v-if="!output.activeSubTab || output.activeSubTab === 'config'" class="grid grid-cols-3 gap-x-6 gap-y-4 pt-2 animate-in fade-in duration-200">
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Transport Stream ID <i class="bi bi-info-circle ml-0.5" title="The value of the transport stream ID field in the Program Map Table."></i></label>
                  <input v-model.number="getHlsSettings(output).M3u8Settings.TransportStreamId" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Program Number <i class="bi bi-info-circle ml-0.5" title="The value of the program number field in the Program Map Table. Default is 1."></i></label>
                  <input v-model.number="getHlsSettings(output).M3u8Settings.ProgramNum" type="number" placeholder="1" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Audio Frames Per PES <i class="bi bi-info-circle ml-0.5" title="The number of audio frames to insert for each PES packet. Default is 4."></i></label>
                  <input v-model.number="getHlsSettings(output).M3u8Settings.AudioFramesPerPes" type="number" placeholder="4" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
              </div>

              <!-- PID Settings Tab -->
              <div v-if="output.activeSubTab === 'pid'" class="grid grid-cols-4 gap-x-4 gap-y-4 pt-2 animate-in fade-in duration-200">
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">PAT Interval <i class="bi bi-info-circle ml-0.5" title="The number of milliseconds between instances of this table in the output transport stream. A value of 0 writes out the PMT once per segment file."></i></label>
                  <input v-model.number="getHlsSettings(output).M3u8Settings.PatInterval" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">PMT PID <i class="bi bi-info-circle ml-0.5" title="Packet Identifier (PID) for the Program Map Table (PMT) in the transport stream. Default is 480."></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.PmtPid" type="text" placeholder="480" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30 text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">PMT Interval <i class="bi bi-info-circle ml-0.5" title="The number of milliseconds between instances of this table in the output transport stream. A value of 0 writes out the PMT once per segment file."></i></label>
                  <input v-model.number="getHlsSettings(output).M3u8Settings.PmtInterval" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Video PID <i class="bi bi-info-circle ml-0.5" title="Packet Identifier (PID) of the elementary video stream in the transport stream. Default is 481."></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.VideoPid" type="text" placeholder="481" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30 text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5 col-span-2">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">PCR Control <i class="bi bi-info-circle ml-0.5" title="When set to pcrEveryPesPacket, a Program Clock Reference value is inserted for every PES header."></i></label>
                  <select v-model="getHlsSettings(output).M3u8Settings.PcrControl" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                    <option value="CONFIGURED_PCR_PERIOD">CONFIGURED_PCR_PERIOD</option>
                    <option value="PCR_EVERY_PES_PACKET">PCR_EVERY_PES_PACKET</option>
                  </select>
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">PCR PID <i class="bi bi-info-circle ml-0.5" title="Packet Identifier (PID) of the Program Clock Reference (PCR) in the transport stream."></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.PcrPid" type="text" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30 text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">PCR Period <i class="bi bi-info-circle ml-0.5" title="Maximum time in milliseconds between Program Clock References (PCRs) inserted into the transport stream."></i></label>
                  <input v-model.number="getHlsSettings(output).M3u8Settings.PcrPeriod" type="number" class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Audio PIDs <i class="bi bi-info-circle ml-0.5" title="Packet Identifier (PID) of the elementary audio stream(s) in the transport stream. Multiple values are accepted, e.g. 492-498."></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.AudioPids" type="text" placeholder="492-498" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30 text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5" title="If set to passthrough, passes any SCTE-35 signals from the input source to this output.">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">SCTE-35 Behavior <i class="bi bi-info-circle ml-0.5"></i></label>
                  <select v-model="getHlsSettings(output).M3u8Settings.Scte35Behavior" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] focus:border-[#ffaa00]/30 outline-none" @change="updateJson">
                    <option value="NO_PASSTHROUGH">NO_PASSTHROUGH</option>
                    <option value="PASSTHROUGH">PASSTHROUGH</option>
                  </select>
                </div>
                <div class="space-y-1.5" title="Packet Identifier (PID) of the SCTE-35 stream in the transport stream.">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">SCTE-35 PID <i class="bi bi-info-circle ml-0.5" title="Default is 500"></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.Scte35Pid" type="text" placeholder="500" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30 text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5" title="Set to PASSTHROUGH to enable ID3 metadata insertion.">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Timed Metadata Behavior <i class="bi bi-info-circle ml-0.5"></i></label>
                  <select v-model="getHlsSettings(output).M3u8Settings.TimedMetadataBehavior" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                    <option value="NO_PASSTHROUGH">NO_PASSTHROUGH</option>
                    <option value="PASSTHROUGH">PASSTHROUGH</option>
                  </select>
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Timed Metadata PIDs <i class="bi bi-info-circle ml-0.5" title="Packet Identifier (PID) of the timed metadata stream in the transport stream. Default is 502."></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.TimedMetadataPid" type="text" placeholder="502" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30 text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">KLV Data PIDs <i class="bi bi-info-circle ml-0.5" title="Packet Identifier (PID) for input source KLV data to this output."></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.KlvDataPids" type="text" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#ffaa00] font-mono outline-none focus:border-[#ffaa00]/30 text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">KLV Behavior <i class="bi bi-info-circle ml-0.5" title="If set to passthrough, passes any KLV data from the input source to this output."></i></label>
                  <select v-model="getHlsSettings(output).M3u8Settings.KlvBehavior" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                    <option value="NO_PASSTHROUGH">NO_PASSTHROUGH</option>
                    <option value="PASSTHROUGH">PASSTHROUGH</option>
                  </select>
                </div>
                <div class="space-y-1.5 opacity-50" title="This parameter is unused and deprecated.">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">DEPRECATED: ECM PID <i class="bi bi-info-circle ml-0.5"></i></label>
                  <input v-model="getHlsSettings(output).M3u8Settings.EcmPid" type="text" disabled class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#e5eefc] cursor-not-allowed outline-none text-center" @input="updateJson" />
                </div>
                <div class="space-y-1.5">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Nielsen ID3 Behavior <i class="bi bi-info-circle ml-0.5" title="If set to passthrough, Nielsen inaudible tones for media tracking will be detected in the input audio and an equivalent ID3 tag will be inserted in the output."></i></label>
                  <select v-model="getHlsSettings(output).M3u8Settings.NielsenId3Behavior" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] outline-none focus:border-[#ffaa00]/30" @change="updateJson">
                    <option value="NO_PASSTHROUGH">NO_PASSTHROUGH</option>
                    <option value="PASSTHROUGH">PASSTHROUGH</option>
                  </select>
                </div>
              </div>
            </div>

              <!-- fMP4 Specific Settings -->
            <div v-if="output.OutputSettings.HlsOutputSettings.HlsSettingsType === 'Fmp4HlsSettings'" class="bg-black/20 p-4 rounded-sm border border-[#2a333d] space-y-4 mb-4">
              <div class="flex items-center justify-between border-b border-[#2a333d] pb-3 mb-2">
                <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-1">
                  <i class="bi bi-gear-fill text-[#ffaa00]"></i> fMP4 HLS Settings
                </div>
              </div>

              <div class="grid grid-cols-2 gap-x-6 gap-y-4">
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Audio Rendition Set <i class="bi bi-info-circle ml-1" title="program_audio"></i></label>
                  <input
                    v-model="getHlsSettings(output).AudioRenditionSets"
                    type="text"
                    placeholder="program_audio"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @input="updateJson"
                  />
                  <p class="text-xs text-[#cdd6ee] mt-1 italic leading-tight">List all audio group IDs used with this video, separated by commas.</p>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5" title="If set to passthrough, Nielsen inaudible tones for media tracking will be detected in the input audio and an equivalent ID3 tag will be inserted in the output.">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Nielsen ID3 Behavior <i class="bi bi-info-circle ml-0.5"></i></label>
                  <select v-model="getHlsSettings(output).NielsenId3Behavior" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] focus:border-[#ffaa00]/30 outline-none" @change="updateJson">
                    <option value="NO_PASSTHROUGH">NO_PASSTHROUGH</option>
                    <option value="PASSTHROUGH">PASSTHROUGH</option>
                  </select>
                </div>
                <div class="space-y-1.5" title="Set to PASSTHROUGH to enable ID3 metadata insertion.">
                  <label class="text-xs text-[#cdd6ee] uppercase font-black ml-1">Timed Metadata Behavior <i class="bi bi-info-circle ml-0.5"></i></label>
                  <select v-model="getHlsSettings(output).TimedMetadataBehavior" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-1.5 text-xs text-[#cdd6ee] focus:border-[#ffaa00]/30 outline-none" @change="updateJson">
                    <option value="NO_PASSTHROUGH">NO_PASSTHROUGH</option>
                    <option value="PASSTHROUGH">PASSTHROUGH</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Audio Only Specific Settings -->
            <div v-if="output.OutputSettings.HlsOutputSettings.HlsSettingsType === 'AudioOnlyHlsSettings'" class="bg-black/20 p-4 rounded-sm border border-[#2a333d] space-y-4 mb-4">
              <div class="flex items-center justify-between border-b border-[#2a333d] pb-3 mb-2">
                <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-1">
                  <i class="bi bi-music-note-beamed text-[#ffaa00]"></i> AUDIO ONLY HLS SETTINGS
                </div>
              </div>

              <div class="grid grid-cols-2 gap-x-6 gap-y-4">
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Audio Group ID <i class="bi bi-info-circle ml-0.5" title="The ID of the audio group to use for this output."></i></label>
                  <input
                    v-model="getHlsSettings(output).AudioGroupId"
                    type="text"
                    placeholder="program_audio"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)]"
                    @input="updateJson"
                  />
                </div>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Segment Type <i class="bi bi-info-circle ml-0.5" title="Choice between AAC and FMP4 segments."></i></label>
                  <select v-model="getHlsSettings(output).SegmentType" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-2 text-xs text-[#cdd6ee] focus:border-[#ffaa00]/30 outline-none" @change="updateJson">
                    <option value="AAC">AAC</option>
                    <option value="FMP4">FMP4</option>
                  </select>
                </div>
                <div class="space-y-1.5 col-span-2">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Audio Track Type <i class="bi bi-info-circle ml-0.5" title="Specifies the type of audio track for this output."></i></label>
                  <select v-model="getHlsSettings(output).AudioTrackType" class="w-full bg-black/20 border border-[#2a333d] rounded-lg px-3 py-2 text-xs text-[#cdd6ee] focus:border-[#ffaa00]/30 outline-none" @change="updateJson">
                    <option value="ALTERNATE_AUDIO_AUTO_SELECT">ALTERNATE_AUDIO_AUTO_SELECT</option>
                    <option value="ALTERNATE_AUDIO_AUTO_SELECT_DEFAULT">ALTERNATE_AUDIO_AUTO_SELECT_DEFAULT</option>
                    <option value="ALTERNATE_AUDIO_NOT_AUTO_SELECT">ALTERNATE_AUDIO_NOT_AUTO_SELECT</option>
                    <option value="AUDIO_ONLY_VARIANT_STREAM">AUDIO_ONLY_VARIANT_STREAM</option>
                  </select>
                </div>
              </div>

              <div class="pt-2 border-t border-[#2a333d] mt-2">
                <h6 class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest mb-3">Audio Only Image</h6>
                <div class="space-y-1.5">
                  <label class="text-[10px] text-[#cdd6ee] uppercase font-black tracking-widest ml-1">Image URI <i class="bi bi-info-circle ml-1" title="URI to a JPEG image that will be displayed when the audio track is active and no video is present."></i></label>
                  <input
                    v-model="getHlsSettings(output).AudioOnlyImage.Uri"
                    type="text"
                    placeholder="s3://bucket/image.jpg"
                    class="h-8 max-h-8 min-h-6 w-full rounded-sm border bg-black/20 px-2.5 text-xs text-[#e5eefc] transition-[border-color,box-shadow] duration-150 outline-none disabled:bg-[#151b21] disabled:text-slate-400 border-[#2a333d] focus:border-[#ffaa00] focus:shadow-[0_0_0_2px_rgba(255,170,0,0.18)] focus:border-[#ffaa00]/30 outline-none"
                    @input="updateJson"
                  />
                </div>
              </div>
            </div>

            <!-- Frame Capture Specific Settings -->
            <div v-if="output.OutputSettings.HlsOutputSettings.HlsSettingsType === 'FrameCaptureHlsSettings'" class="bg-black/20 p-4 rounded-sm border border-[#2a333d] space-y-4 mb-4">
              <div class="flex items-center justify-between border-b border-[#2a333d] pb-3 mb-2">
                <div class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest flex items-center gap-1">
                  <i class="bi bi-camera-fill text-[#ffaa00]"></i> FRAME CAPTURE HLS SETTINGS
                </div>
              </div>
              <div class="py-4 text-center">
                <p class="text-[10px] text-[#cdd6ee] font-bold uppercase tracking-widest italic">No specific settings available for Frame Capture HLS here. Settings are managed at the output group and video description levels.</p>
              </div>
            </div> <!-- Frame Capture -->
            </template> <!-- end v-if HLS output settings -->

            <!-- ARCHIVE-specific output settings panel -->
            <div v-if="config.outputGroup.type === 'ARCHIVE'" class="bg-black/20 p-4 rounded-sm border border-[#2a333d] space-y-4 mb-4">
              <div class="flex items-center gap-2 border-b border-[#2a333d] pb-3 mb-2">
                <i class="bi bi-archive-fill text-[#ffaa00] text-[9px]"></i>
                <span class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest">Archive Output Settings</span>
              </div>
              <div class="py-4 text-center">
                <p class="text-[10px] text-[#cdd6ee] font-bold uppercase tracking-widest italic">Container (M2TS) settings are managed separately. Use the Name Modifier and File Extension fields above.</p>
              </div>
            </div> <!-- ARCHIVE output settings -->

            <!-- RTMP-specific output settings panel -->
            <div v-if="config.outputGroup.type === 'RTMP'" class="bg-black/20 p-4 rounded-sm border border-[#2a333d] space-y-4 mb-4">
              <div class="flex items-center gap-2 border-b border-[#2a333d] pb-3 mb-2">
                <i class="bi bi-broadcast text-[#ffaa00] text-[9px]"></i>
                <span class="text-[9px] font-black text-[#cdd6ee] uppercase tracking-widest">RTMP Output Settings</span>
              </div>
              <div class="py-4 text-center">
                <p class="text-[10px] text-[#cdd6ee] font-bold uppercase tracking-widest italic">RTMP destination and connection settings are configured in the fields above.</p>
              </div>
            </div> <!-- RTMP output settings -->

          </div> <!-- v-show output -->
          </div> <!-- v-for output -->
        </div> <!-- grid outputs -->
      </section>
    </div> <!-- main -->
  </div> <!-- outer -->
</template>

<script>
export default {
  name: "TranscodingOutputConfigEditor",
  props: {
    value: {
      type: [String, Object],
      default: () => ({}),
    },
  },
  data() {
    return {
      validationErrors: [],
      activeHlsSubTab: 'location',
      activeArchiveSubTab: 'location',
      activeOutputSubTabs: {},
      codecTabs: [
        { id: 'ratio', label: 'Aspect Ratio' },
        { id: 'rate', label: 'Rate Control' },
        { id: 'frame', label: 'Frame Rate' },
        { id: 'gop', label: 'GOP Structure' },
        { id: 'details', label: 'Codec Details' },
        { id: 'timecode', label: 'Timecode' },
        { id: 'colorspace', label: 'Color Space' },
        { id: 'additional', label: 'Additional Encoding' }
      ],
      temporalStrengths: ['AUTO', ...Array.from({ length: 16 }, (_, i) => `STRENGTH_${i + 1}`)],
      bandwidthStrengths: ['AUTO', 'STRENGTH_1', 'STRENGTH_2', 'STRENGTH_3', 'STRENGTH_4'],
      bandwidthSharpening: ['DISABLED', 'SHARPENING_1', 'SHARPENING_2', 'SHARPENING_3'],
      h264Profiles: ['BASELINE', 'HIGH', 'HIGH_10BIT', 'HIGH_422', 'HIGH_422_10BIT', 'MAIN'],
      h264Levels: [
        'H264_LEVEL_1', 'H264_LEVEL_1_1', 'H264_LEVEL_1_2', 'H264_LEVEL_1_3',
        'H264_LEVEL_2', 'H264_LEVEL_2_1', 'H264_LEVEL_2_2',
        'H264_LEVEL_3', 'H264_LEVEL_3_1', 'H264_LEVEL_3_2',
        'H264_LEVEL_4', 'H264_LEVEL_4_1', 'H264_LEVEL_4_2',
        'H264_LEVEL_5', 'H264_LEVEL_5_1', 'H264_LEVEL_5_2',
        'H264_LEVEL_AUTO'
      ],
      afdOptions: [
        "AFD_0000", "AFD_0010 (16:9 bottom)", "AFD_0011 (16:9 top)", "AFD_0100 (16:9 center)",
        "AFD_1000 (4:3 center)", "AFD_1001 (4:3 top)", "AFD_1010 (4:3 center)", "AFD_1011 (4:3 center)",
        "AFD_1101 (4:3 center)", "AFD_1110 (16:9 center)", "AFD_1111 (16:9 center)"
      ],

      h265Levels: [
        'H265_LEVEL_1', 'H265_LEVEL_2', 'H265_LEVEL_2_1', 'H265_LEVEL_3', 'H265_LEVEL_3_1',
        'H265_LEVEL_4', 'H265_LEVEL_4_1', 'H265_LEVEL_5', 'H265_LEVEL_5_1', 'H265_LEVEL_5_2',
        'H265_LEVEL_6', 'H265_LEVEL_6_1', 'H265_LEVEL_6_2', 'H265_LEVEL_AUTO'
      ],
      config: this.parseValue(this.value),
      activeTab: "location",
      audioCodecTabs: {
        AacSettings: [
          { id: 'config', label: 'Configuration' }
        ],
        Ac3Settings: [
          { id: 'config', label: 'Config' },
          { id: 'enc_params', label: 'Encoder Parameters' },
          { id: 'meta_handling', label: 'Metadata Handling' }
        ],
        Eac3Settings: [
          { id: 'config', label: 'Config' },
          { id: 'enc_params', label: 'Encoder Parameters' },
          { id: 'meta_handling', label: 'Metadata Handling' }
        ],
        Eac3AtmosSettings: [
          { id: 'config', label: 'Config' },
          { id: 'meta_handling', label: 'Metadata Handling' }
        ],
        Mp2Settings: [{ id: 'config', label: 'Config' }],
        WavSettings: [{ id: 'config', label: 'Config' }],
        PassThroughSettings: []
      },
      tabs: [
        { id: "location", label: "Location" },
        { id: "manifest", label: "Manifest & Segments" },
        { id: "captions", label: "Captions" },
        { id: "id3", label: "ID3" },
      ],
    };
  },
  computed: {
    hlsGroupSettings() {
      if (this.config.outputGroup?.type === 'HLS') {
        return this.config.outputGroup.outputGroupSettings.HlsGroupSettings;
      }
      return null;
    },
    rtmpGroupSettings() {
      if (this.config.outputGroup?.type === 'RTMP') {
        return this.config.outputGroup.outputGroupSettings.RtmpGroupSettings;
      }
      return null;
    }
  },
  watch: {
    value: {
      handler(newVal) {
        const parsed = this.parseValue(newVal);
        if (JSON.stringify(parsed) !== JSON.stringify(this.config)) {
          this.config = parsed;
          // Initial validation on load
          this.$nextTick(() => {
            const isValid = this.validateForm();
            this.$emit("validation-change", { isValid, errors: this.validationErrors });
          });
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.updateJson();
  },
  methods: {
    toggleVideoMinimize(video, event) {
      this.withPreservedScroll(event, () => {
        if (typeof video.isMinimized === 'undefined') {
          this.$set(video, 'isMinimized', true);
        } else {
          video.isMinimized = !video.isMinimized;
        }
      });
    },
    toggleAudioMinimize(audio, event) {
      this.withPreservedScroll(event, () => {
        if (typeof audio.isMinimized === 'undefined') {
          this.$set(audio, 'isMinimized', true);
        } else {
          audio.isMinimized = !audio.isMinimized;
        }
      });
    },
    toggleOutputMinimize(output, event) {
      this.withPreservedScroll(event, () => {
        if (typeof output.isMinimized === 'undefined') {
          this.$set(output, 'isMinimized', true);
        } else {
          output.isMinimized = !output.isMinimized;
        }
      });
    },
    withPreservedScroll(event, mutator) {
      const scroller = this.findScrollableParent(event?.currentTarget);
      const previousScrollerTop = scroller ? scroller.scrollTop : null;
      const previousWindowTop =
        typeof window !== "undefined" ? window.pageYOffset || document.documentElement.scrollTop || 0 : 0;

      mutator();

      this.$nextTick(() => {
        if (scroller) {
          scroller.scrollTop = previousScrollerTop;
        }
        if (typeof window !== "undefined") {
          window.scrollTo(0, previousWindowTop);
        }
      });
    },
    findScrollableParent(element) {
      if (!element || typeof window === "undefined") return null;

      let node = element.parentElement;
      while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const isScrollable = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
        if (isScrollable) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    },
    setVideoTab(video, tab) {
      if (typeof video.activeCodecTab === 'undefined') {
        this.$set(video, 'activeCodecTab', tab);
      } else {
        video.activeCodecTab = tab;
      }
    },
    setAudioTab(audio, tab) {
      if (typeof audio.activeAudioTab === 'undefined') {
        this.$set(audio, 'activeAudioTab', tab);
      } else {
        audio.activeAudioTab = tab;
      }
    },
    getAudioCodecTabs(audio) {
      return this.audioCodecTabs[audio.codecType] || [];
    },
    getDolbyCodingModes(type) {
      if (type === 'Ac3Settings') {
        return ['CODING_MODE_1_0', 'CODING_MODE_1_1', 'CODING_MODE_2_0', 'CODING_MODE_3_2_LFE'];
      }
      if (type === 'Eac3Settings') {
        return ['CODING_MODE_1_0', 'CODING_MODE_2_0', 'CODING_MODE_3_2'];
      }
      if (type === 'Eac3AtmosSettings') {
        return ['CODING_MODE_5_1_4', 'CODING_MODE_7_1_4', 'CODING_MODE_9_1_6'];
      }
      return [];
    },
    getNielsenTimezones() {
      return ['USA_ALASKA', 'USA_CENTRAL', 'USA_EASTERN', 'USA_HAWAII', 'USA_MOUNTAIN', 'USA_PACIFIC', 'USA_PUERTO_RICO', 'USA_SAMOA', 'UTC'];
    },
    toggleRemixSettings(audio) {
      if (audio.RemixSettings) {
        this.$set(audio, 'RemixSettings', undefined);
      } else {
        this.$set(audio, 'RemixSettings', {
          ChannelsIn: 16,
          ChannelsOut: 2,
          ChannelMappings: []
        });
      }
      this.updateJson();
    },
    toggleNormalizationSettings(audio) {
      if (audio.AudioNormalizationSettings) {
        this.$set(audio, 'AudioNormalizationSettings', undefined);
      } else {
        this.$set(audio, 'AudioNormalizationSettings', {
          Algorithm: 'ITU_1770_1',
          AlgorithmControl: 'CORRECT_AUDIO',
          TargetLkfs: undefined
        });
      }
      this.updateJson();
    },
    addRemixMapping(audio) {
      if (!audio.RemixSettings.ChannelMappings) {
        this.$set(audio.RemixSettings, 'ChannelMappings', []);
      }
      audio.RemixSettings.ChannelMappings.push({
        OutputChannel: audio.RemixSettings.ChannelMappings.length,
        InputChannelLevels: []
      });
      this.updateJson();
      //
    },
    onWatermarkChange(audio) {
      if (!audio.activeWatermark) {
        this.$set(audio, 'AudioWatermarkSettings', undefined);
      } else {
        const settings = {};
        if (audio.activeWatermark === 'NielsenWatermarksSettings') {
          settings.NielsenWatermarksSettings = {
            NielsenDistributionType: "PROGRAM_CONTENT",
            NielsenCbetSettings: undefined,
            NielsenNaesIiNwSettings: []
          };
        }
        this.$set(audio, 'AudioWatermarkSettings', settings);
      }
      this.updateJson();
    },
    toggleCbet(audio) {
      const nielsen = audio.AudioWatermarkSettings.NielsenWatermarksSettings;
      if (nielsen.NielsenCbetSettings) {
        this.$set(nielsen, 'NielsenCbetSettings', undefined);
      } else {
        this.$set(nielsen, 'NielsenCbetSettings', {
          Csid: "",
          CbetCheckDigitString: "",
          CbetStepaside: "DISABLED"
        });
      }
      this.updateJson();
    },
    addNaesIiNw(audio) {
      const nielsen = audio.AudioWatermarkSettings.NielsenWatermarksSettings;
      if (!nielsen.NielsenNaesIiNwSettings) {
        this.$set(nielsen, 'NielsenNaesIiNwSettings', []);
      }
      nielsen.NielsenNaesIiNwSettings.push({
        Sid: 1,
        CheckDigitString: "0",
        Timezone: "UTC"
      });
      this.updateJson();
    },
    removeNaesIiNw(audio, index) {
      audio.AudioWatermarkSettings.NielsenWatermarksSettings.NielsenNaesIiNwSettings.splice(index, 1);
      this.updateJson();
    },
    removeRemixMapping(audio, index) {
      if (audio.RemixSettings && audio.RemixSettings.ChannelMappings) {
        audio.RemixSettings.ChannelMappings.splice(index, 1);
        this.updateJson();
        //
      }
    },
    removeInputLevel(map, index) {
      if (map.InputChannelLevels) {
        map.InputChannelLevels.splice(index, 1);
        this.updateJson();
        //
      }
    },
    getNielsenTimezones() {
      return [
        "AMERICA_PUERTO_RICO", "US_ALASKA", "US_ARIZONA", "US_CENTRAL",
        "US_EASTERN", "US_HAWAII", "US_MOUNTAIN", "US_PACIFIC", "US_SAMOA", "UTC"
      ];
    },
    removeDashRole(audio, index) {
      if (audio.AudioDashRoles && audio.AudioDashRoles.length > index) {
        audio.AudioDashRoles.splice(index, 1);
        this.$nextTick(() => {
          this.updateJson();
          //
        });
      }
    },
    setSubTab(output, tab) {
      if (typeof output.activeSubTab === 'undefined') {
        this.$set(output, 'activeSubTab', tab);
      } else {
        output.activeSubTab = tab;
      }
      //
    },
    onColorSpaceChange(video) {
      const type = video.codecType;
      if (!video.CodecSettings[type]) return;

      if (!video.activeColorSpace) {
        this.$set(video.CodecSettings[type], 'ColorSpaceSettings', undefined);
      } else {
        const settings = {};
        if (video.activeColorSpace === 'Hdr10Settings') {
          settings.Hdr10Settings = { MaxCll: undefined, MaxFall: undefined };
        } else {
          settings[video.activeColorSpace] = {};
        }
        this.$set(video.CodecSettings[type], 'ColorSpaceSettings', settings);
      }
      this.updateJson();
    },
    onFilterTypeChange(video) {
      const type = video.codecType;
      if (!video.CodecSettings[type]) return;

      if (!video.activeFilterType) {
        this.$set(video.CodecSettings[type], 'FilterSettings', undefined);
      } else {
        const settings = {};
        if (video.activeFilterType === 'TemporalFilterSettings') {
          settings.TemporalFilterSettings = {
            PostFilterSharpening: "AUTO",
            Strength: "AUTO"
          };
        } else if (video.activeFilterType === 'BandwidthReductionFilterSettings') {
          settings.BandwidthReductionFilterSettings = {
            PostFilterSharpening: "DISABLED",
            Strength: "AUTO"
          };
        }
        this.$set(video.CodecSettings[type], 'FilterSettings', settings);
      }
      this.updateJson();
    },
    toggleTimecodeBurnin(video) {
      if (video.CodecSettings[video.codecType].TimecodeBurninSettings) {
        this.$set(video.CodecSettings[video.codecType], 'TimecodeBurninSettings', undefined);
      } else {
        this.$set(video.CodecSettings[video.codecType], 'TimecodeBurninSettings', {
          FontSize: "SMALL_16",
          Position: "BOTTOM_RIGHT",
          Prefix: ""
        });
      }
      this.updateJson();
    },
    parseValue(val) {
      try {
        let parsed = typeof val === 'string' ? JSON.parse(val || '{}') : val;

        const defaultConfig = this.getDefaultConfig();

        const merged = {
          destinations: (parsed.destinations || []).map(d => {
            const settings = (d.Settings || []).map(s => ({ Url: s.Url || "", StreamName: s.StreamName || "" }));
            while (settings.length < 2) settings.push({ Url: "", StreamName: "" });
            return {
              Id: d.Id || "",
              Settings: settings.slice(0, 2)
            };
          }),
          videoDescriptions: (parsed.videoDescriptions || []).map(vd => this.normalizeVideoDescription(vd)),
          audioDescriptions: (parsed.audioDescriptions || []).map(ad => this.normalizeAudioDescription(ad)),
          outputGroup: {
            name: parsed.outputGroup?.name || parsed.name || defaultConfig.outputGroup.name,
            type: parsed.outputGroup?.type || parsed.type || 'HLS',
            outputGroupSettings: {
              HlsGroupSettings: {
                ...defaultConfig.outputGroup.outputGroupSettings.HlsGroupSettings,
                ...(parsed.outputGroup?.outputGroupSettings?.HlsGroupSettings || parsed.outputGroupSettings?.HlsGroupSettings || {})
              },
              ArchiveGroupSettings: {
                ...defaultConfig.outputGroup.outputGroupSettings.ArchiveGroupSettings,
                ...(parsed.outputGroup?.outputGroupSettings?.ArchiveGroupSettings || parsed.outputGroupSettings?.ArchiveGroupSettings || {})
              },
              RtmpGroupSettings: {
                ...defaultConfig.outputGroup.outputGroupSettings.RtmpGroupSettings,
                ...(parsed.outputGroup?.outputGroupSettings?.RtmpGroupSettings || parsed.outputGroupSettings?.RtmpGroupSettings || {})
              }
            },
            outputs: (parsed.outputGroup?.outputs || parsed.outputs || []).map(o => this.normalizeOutput(o))
          }
        };

        if (merged.outputGroup.outputs.length === 0) {
          merged.outputGroup.outputs.push(this.createDefaultOutput());
        }

        return merged;
      } catch (e) {
        console.error("Failed to parse Transcoding Output Config:", e);
        return this.getDefaultConfig();
      }
    },
    normalizeOutput(o) {
      const def = this.createDefaultOutput();

      const hlsSettings = o.OutputSettings?.HlsOutputSettings?.HlsSettings || {};
      let hlsType = "StandardHlsSettings";
      if (hlsSettings.Fmp4HlsSettings) hlsType = "Fmp4HlsSettings";
      else if (hlsSettings.AudioOnlyHlsSettings) hlsType = "AudioOnlyHlsSettings";
      else if (hlsSettings.FrameCaptureHlsSettings) hlsType = "FrameCaptureHlsSettings";

      return {
        OutputName: o.OutputName || def.OutputName,
        VideoDescriptionName: o.VideoDescriptionName || "",
        AudioDescriptionNames: o.AudioDescriptionNames || [],
        OutputSettings: {
          ArchiveOutputSettings: {
            ContainerSettings: o.OutputSettings?.ArchiveOutputSettings?.ContainerSettings || { M2tsSettings: {} },
            Extension: o.OutputSettings?.ArchiveOutputSettings?.Extension || "",
            NameModifier: o.OutputSettings?.ArchiveOutputSettings?.NameModifier || ""
          },
          RtmpOutputSettings: {
            CertificateMode: o.OutputSettings?.RtmpOutputSettings?.CertificateMode || "SELF_SIGNED",
            ConnectionRetryInterval: o.OutputSettings?.RtmpOutputSettings?.ConnectionRetryInterval ?? 2,
            Destination: o.OutputSettings?.RtmpOutputSettings?.Destination || { DestinationRefId: "FROM_TEMPLATE" },
            NumRetries: o.OutputSettings?.RtmpOutputSettings?.NumRetries ?? 10,
          },
          HlsOutputSettings: {
            HlsSettingsType: hlsType,
            H265PackagingType: o.OutputSettings?.HlsOutputSettings?.H265PackagingType || "HVC1",
            NameModifier: o.OutputSettings?.HlsOutputSettings?.NameModifier || "",
            SegmentModifier: o.OutputSettings?.HlsOutputSettings?.SegmentModifier || "",
            HlsSettings: {
              HlsSettingsChoice: hlsSettings.HlsSettingsChoice || "STANDARD_HLS",
              StandardHlsSettings: {
                AudioRenditionSets: hlsSettings.StandardHlsSettings?.AudioRenditionSets || "program_audio",
                M3u8Settings: {
                  AudioFramesPerPes: hlsSettings.StandardHlsSettings?.M3u8Settings?.AudioFramesPerPes ?? 2,
                  AudioPids: hlsSettings.StandardHlsSettings?.M3u8Settings?.AudioPids || "492-498",
                  NielsenId3Behavior: hlsSettings.StandardHlsSettings?.M3u8Settings?.NielsenId3Behavior || "NO_PASSTHROUGH",
                  PatInterval: hlsSettings.StandardHlsSettings?.M3u8Settings?.PatInterval ?? undefined,
                  PcrControl: hlsSettings.StandardHlsSettings?.M3u8Settings?.PcrControl || "PCR_EVERY_PES_PACKET",
                  PcrPeriod: hlsSettings.StandardHlsSettings?.M3u8Settings?.PcrPeriod ?? undefined,
                  PcrPid: hlsSettings.StandardHlsSettings?.M3u8Settings?.PcrPid || undefined,
                  PmtInterval: hlsSettings.StandardHlsSettings?.M3u8Settings?.PmtInterval ?? undefined,
                  PmtPid: hlsSettings.StandardHlsSettings?.M3u8Settings?.PmtPid || "480",
                  ProgramNum: hlsSettings.StandardHlsSettings?.M3u8Settings?.ProgramNum ?? 1,
                  Scte35Behavior: hlsSettings.StandardHlsSettings?.M3u8Settings?.Scte35Behavior || "NO_PASSTHROUGH",
                  Scte35Pid: hlsSettings.StandardHlsSettings?.M3u8Settings?.Scte35Pid || "500",
                  TimedMetadataBehavior: hlsSettings.StandardHlsSettings?.M3u8Settings?.TimedMetadataBehavior || "NO_PASSTHROUGH",
                  TimedMetadataPid: hlsSettings.StandardHlsSettings?.M3u8Settings?.TimedMetadataPid || "502",
                  TransportStreamId: hlsSettings.StandardHlsSettings?.M3u8Settings?.TransportStreamId ?? undefined,
                  VideoPid: hlsSettings.StandardHlsSettings?.M3u8Settings?.VideoPid || "481",
                  KlvBehavior: hlsSettings.StandardHlsSettings?.M3u8Settings?.KlvBehavior || "NO_PASSTHROUGH",
                  KlvDataPids: hlsSettings.StandardHlsSettings?.M3u8Settings?.KlvDataPids || undefined,
                }
              },
              Fmp4HlsSettings: hlsSettings.Fmp4HlsSettings ? {
                AudioRenditionSets: hlsSettings.Fmp4HlsSettings.AudioRenditionSets || "program_audio",
                NielsenId3Behavior: hlsSettings.Fmp4HlsSettings.NielsenId3Behavior || "NO_PASSTHROUGH",
                TimedMetadataBehavior: hlsSettings.Fmp4HlsSettings.TimedMetadataBehavior || "NO_PASSTHROUGH"
              } : undefined,
              AudioOnlyHlsSettings: hlsSettings.AudioOnlyHlsSettings ? {
                AudioGroupId: hlsSettings.AudioOnlyHlsSettings.AudioGroupId || "program_audio",
                AudioTrackType: hlsSettings.AudioOnlyHlsSettings.AudioTrackType || "ALTERNATE_AUDIO_AUTO_SELECT",
                SegmentType: hlsSettings.AudioOnlyHlsSettings.SegmentType || "AAC",
                AudioOnlyImage: {
                  Uri: hlsSettings.AudioOnlyHlsSettings.AudioOnlyImage?.Uri || "",
                  PasswordParam: hlsSettings.AudioOnlyHlsSettings.AudioOnlyImage?.PasswordParam || "",
                  Username: hlsSettings.AudioOnlyHlsSettings.AudioOnlyImage?.Username || ""
                }
              } : undefined,
              FrameCaptureHlsSettings: hlsSettings.FrameCaptureHlsSettings || undefined
            }
          }
        }
      };
    },
    getDefaultConfig() {
      return {
        destinations: [],
        videoDescriptions: [],
        audioDescriptions: [],
        outputGroup: {
          name: "Archive-Group-01",
          type: "HLS",
          outputGroupSettings: {
            HlsGroupSettings: {
              Destination: { DestinationRefId: "FROM_TEMPLATE" },
              DirectoryStructure: "SINGLE_DIRECTORY",
              IndexNSegments: 10,
              ManifestDurationFormat: "FLOATING_POINT",
              Mode: "LIVE",
              TsFileMode: "SEGMENTED_FILES",
              StreamInfResolution: "INCLUDE",
              SegmentLength: 10,
              BaseUrlManifest: "",
              BaseUrlManifest1: "",
              BaseUrlContent: "",
              BaseUrlContent1: "",
              SegmentsPerSubdirectory: 1000,
              CaptionLanguageSetting: "OMIT",
              TimedMetadataId3Frame: "PRIV",
              TimedMetadataId3Period: 10,
              HlsId3SegmentTagging: "DISABLED",
              DiscontinuityTags: "INSERT",
              SegmentationMode: "USE_SEGMENT_DURATION",
              MinSegmentLength: undefined,
              KeepSegments: 21,
              InputLossAction: "EMIT_OUTPUT",
              OutputSelection: "MANIFESTS_AND_SEGMENTS",
              IFrameOnlyPlaylists: "DISABLED",
              ProgramDateTime: "EXCLUDE",
              ProgramDateTimePeriod: 600,
              ProgramDateTimeClock: "INITIALIZE_FROM_OUTPUT_TIMECODE",
              ClientCache: "ENABLED",
              TimestampDeltaMilliseconds: undefined,
              CodecSpecification: "RFC_4281",
              ManifestCompression: "NONE",
              RedundantManifest: "DISABLED",
              IncompleteSegmentBehavior: "AUTO"
            },
            ArchiveGroupSettings: {
              Destination: { DestinationRefId: "FROM_TEMPLATE" },
              RolloverInterval: 300,
            },
            RtmpGroupSettings: {
              AdMarkers: [],
              AuthenticationScheme: "COMMON",
              CacheFullBehavior: "DISCONNECT_IMMEDIATELY",
              CacheLength: 30,
              CaptionData: "ALL",
              InputLossAction: "EMIT_OUTPUT",
              RestartDelay: 15,
              IncludeFillerNalUnits: "AUTO",
            },
          },
          outputs: [this.createDefaultOutput()],
        },
      };
    },
    normalizeVideoDescription(video) {
        if (typeof video.codecType === 'undefined') video.codecType = null;
        if (!video.activeColorSpace) video.activeColorSpace = null;
        if (!video.activeFilterType) video.activeFilterType = null;
        if (!video.CodecSettings) video.CodecSettings = {};

        const type = video.codecType;
        if (type && video.CodecSettings[type]?.ColorSpaceSettings) {
          const colorSettings = video.CodecSettings[type].ColorSpaceSettings;
          if (colorSettings.ColorSpacePassthroughSettings) video.activeColorSpace = "ColorSpacePassthroughSettings";
          else if (colorSettings.Rec601Settings) video.activeColorSpace = "Rec601Settings";
          else if (colorSettings.Rec709Settings) video.activeColorSpace = "Rec709Settings";
          else if (colorSettings.Hdr10Settings) video.activeColorSpace = "Hdr10Settings";
          else if (colorSettings.Hlg2020Settings) video.activeColorSpace = "Hlg2020Settings";
          else if (colorSettings.DolbyVision81Settings) video.activeColorSpace = "DolbyVision81Settings";
        }

        if (type && video.CodecSettings[type]?.FilterSettings) {
          const filterSettings = video.CodecSettings[type].FilterSettings;
          if (filterSettings.TemporalFilterSettings) video.activeFilterType = "TemporalFilterSettings";
          else if (filterSettings.BandwidthReductionFilterSettings) video.activeFilterType = "BandwidthReductionFilterSettings";
        }

        if (video.codecType === 'H265Settings') {
          if (!video.CodecSettings.H265Settings) {
            video.CodecSettings.H265Settings = {
              AdaptiveQuantization: "HIGH",
              AfdSignaling: "NONE",
              AlternativeTransferFunction: "OMIT",
              Bitrate: 5000000,
              BufSize: 10000000,
              ColorMetadata: "INSERT",
              Deblocking: "ENABLED",
              FilterSettings: undefined,
              FixedAfd: "AFD_0000",
              FlickerAq: "ENABLED",
              FramerateDenominator: undefined,
              FramerateNumerator: undefined,
              GopBReference: "DISABLED",
              GopClosedCadence: 1,
              GopNumBFrames: 3,
              GopSize: 90,
              GopSizeUnits: "FRAMES",
              Level: "H265_LEVEL_AUTO",
              LookAheadRateControl: "HIGH",
              MaxBitrate: 5000000,
              MinIInterval: 0,
              MinQp: 0,
              ParDenominator: 1,
              ParNumerator: 1,
              Profile: "MAIN",
              QvbrQualityLevel: 2,
              RateControlMode: "CBR",
              ScanType: "PROGRESSIVE",
              SceneChangeDetect: "ENABLED",
              SubgopLength: "FIXED",
              Tier: "MAIN",
              TimecodeInsertion: "DISABLED",
              TimecodeBurninSettings: undefined,
              Slices: 1,
              TileWidth: 1,
              TileHeight: 1,
              TilePadding: "NONE",
              MvTemporalPredictor: "ENABLED",
              MvOverPictureBoundaries: "ENABLED",
              TreeblockSize: "AUTO"
            };
          }
        }
        else if (video.codecType === 'H264Settings') {
          if (!video.CodecSettings.H264Settings) {
            video.CodecSettings.H264Settings = {
              AdaptiveQuantization: "AUTO",
              AfdSignaling: "NONE",
              Bitrate: 5000000,
              BufFillPct: 90,
              BufSize: 10000000,
              ColorMetadata: "INSERT",
              EntropyEncoding: "CABAC",
              FilterSettings: undefined,
              FixedAfd: "AFD_0000",
              FlickerAq: "ENABLED",
              ForceFieldPictures: "DISABLED",
              FramerateControl: "INITIALIZE_FROM_SOURCE",
              FramerateDenominator: 1,
              FramerateNumerator: 30,
              GopBReference: "DISABLED",
              GopClosedCadence: 1,
              GopNumBFrames: 3,
              GopSize: 90,
              GopSizeUnits: "FRAMES",
              Level: "H264_LEVEL_AUTO",
              LookAheadRateControl: "MEDIUM",
              MaxBitrate: 5000000,
              MinIInterval: 0,
              MinQp: 0,
              NumRefFrames: 1,
              ParControl: "INITIALIZE_FROM_SOURCE",
              ParDenominator: undefined,
              ParNumerator: undefined,
              Profile: "MAIN",
              QualityLevel: "ENHANCED_QUALITY",
              QvbrQualityLevel: 2,
              RateControlMode: "CBR",
              ScanType: "PROGRESSIVE",
              SceneChangeDetect: "ENABLED",
              Slices: 1,
              Softness: 0,
              SpatialAq: "ENABLED",
              SubgopLength: "FIXED",
              Syntax: "DEFAULT",
              TemporalAq: "ENABLED",
              TimecodeInsertion: "DISABLED",
              TimecodeBurninSettings: undefined
            };
          }
        } else if (video.codecType === 'FrameCaptureSettings') {
          if (!video.CodecSettings.FrameCaptureSettings) {
            video.CodecSettings.FrameCaptureSettings = {
              CaptureInterval: 10,
              CaptureIntervalUnits: "SECONDS"
            };
          }
        }
        return video;
    },
    addVideoDescription() {
      const newVideo = {
        Name: "",
        Width: undefined,
        Height: undefined,
        codecType: null,
        ScalingBehavior: "DEFAULT",
        Sharpness: 50,
        RespondToAfd: "NONE",
        CodecSettings: {}
      };
      this.normalizeVideoDescription(newVideo);
      this.config.videoDescriptions.push(newVideo);
      this.updateJson();
    },
    removeVideoDescription(index) {
      this.config.videoDescriptions.splice(index, 1);
      this.updateJson();
    },
    addAudioDescription() {
      this.config.audioDescriptions.push(this.normalizeAudioDescription({ Name: "" }));
      this.updateJson();
    },
    removeAudioDescription(index) {
      this.config.audioDescriptions.splice(index, 1);
      this.updateJson();
    },
    setAudioTab(audio, tab) {
      if (typeof audio.activeCodecTab === 'undefined') {
        this.$set(audio, 'activeCodecTab', tab);
      } else {
        audio.activeCodecTab = tab;
      }
      //
    },
    onAudioCodecTypeChange(audio) {
      console.log('--- Audio Codec Type Change ---', audio.codecType);

      const selectedType = audio.codecType;
      // We must preserve the codecType before clearing CodecSettings
      this.$set(audio, 'CodecSettings', {});
      this.$set(audio, 'codecType', selectedType);
      this.$set(audio, 'activeAudioTab', 'config');

      if (!selectedType || selectedType === 'PassThroughSettings') {
        this.updateJson();
        //
        return;
      }

      const defaults = {
        AacSettings: {
          Bitrate: 192000,
          CodingMode: 'CODING_MODE_2_0',
          InputType: 'NORMAL',
          Profile: 'LC',
          RateControlMode: 'CBR',
          RawFormat: 'NONE',
          SampleRate: 48000,
          Spec: 'MPEG4',
          VbrQuality: undefined
        },
        Ac3Settings: {
          Bitrate: 192000,
          BitstreamMode: 'COMPLETE_MAIN',
          CodingMode: 'CODING_MODE_2_0',
          Dialnorm: undefined,
          DrcProfile: 'FILM_STANDARD',
          AttenuationControl: 'NONE',
          LfeFilter: 'DISABLED',
          MetadataControl: 'USE_CONFIGURED'
        },
        Eac3Settings: {
          AttenuationControl: 'NONE',
          Bitrate: 256000,
          BitstreamMode: 'COMPLETE_MAIN',
          CodingMode: 'CODING_MODE_3_2',
          DcFilter: 'ENABLED',
          Dialnorm: undefined,
          DrcLine: 'FILM_STANDARD',
          DrcRf: 'FILM_STANDARD',
          LfeControl: 'LFE',
          LfeFilter: 'ENABLED',
          LoRoCenterMixLevel: -3,
          LoRoSurroundMixLevel: -3,
          LtRtCenterMixLevel: -3,
          LtRtSurroundMixLevel: -3,
          MetadataControl: 'USE_CONFIGURED',
          PassthroughControl: 'NO_PASSTHROUGH',
          PhaseControl: 'SHIFT_90_DEGREES',
          StereoDownmix: 'NOT_INDICATED',
          SurroundExMode: 'DISABLED',
          SurroundMode: 'NOT_INDICATED'
        },
        Eac3AtmosSettings: {
          Bitrate: 448000,
          CodingMode: 'CODING_MODE_5_1_4',
          Dialnorm: undefined,
          DrcLine: 'FILM_STANDARD',
          DrcRf: 'FILM_STANDARD',
          HeightTrim: -3,
          SurroundTrim: -3
        },
        Mp2Settings: {
          Bitrate: 192000,
          CodingMode: 'CODING_MODE_2_0',
          SampleRate: 48000
        },
        WavSettings: {
          BitDepth: 16,
          CodingMode: 'CODING_MODE_2_0',
          SampleRate: 48000
        }
      };

      if (defaults[selectedType]) {
        this.$set(audio.CodecSettings, selectedType, JSON.parse(JSON.stringify(defaults[selectedType])));
      }

      this.updateJson();
      //
    },
    getAudioCodecTabs(audio) {
      if (!audio.codecType) return [];
      return this.audioCodecTabs[audio.codecType] || [];
    },
    removeDashRole(audio, index) {
      audio.AudioDashRoles.splice(index, 1);
      this.updateJson();
      //
    },
    normalizeAudioDescription(ad) {
      const type = ad.CodecSettings ? Object.keys(ad.CodecSettings).find(k => k.endsWith('Settings')) : (ad.codecType || 'AacSettings');

      const res = {
        Name: ad.Name || "audio_1",
        AudioSelectorName: ad.AudioSelectorName || undefined,
        StreamName: ad.StreamName || undefined,
        LanguageCode: ad.LanguageCode || undefined,
        LanguageCodeControl: ad.LanguageCodeControl || 'FOLLOW_INPUT',
        AudioTypeControl: ad.AudioTypeControl || 'FOLLOW_INPUT',
        AudioType: ad.AudioType || undefined,
        activeWatermark: ad.activeWatermark || null,
        AudioWatermarkSettings: ad.AudioWatermarkSettings || undefined,
        AudioDashRoles: ad.AudioDashRoles || [],
        DvbDashAccessibility: ad.DvbDashAccessibility || undefined,
        RemixSettings: ad.RemixSettings ? {
          ChannelsIn: ad.RemixSettings.ChannelsIn || 16,
          ChannelsOut: ad.RemixSettings.ChannelsOut || 2,
          ChannelMappings: (ad.RemixSettings.ChannelMappings || []).map(m => ({
            OutputChannel: m.OutputChannel ?? 0,
            InputChannelLevels: (m.InputChannelLevels || m.Levels || []).map(l => ({
              InputChannel: l.InputChannel ?? 0,
              Gain: l.Gain ?? 0
            }))
          }))
        } : undefined,
        AudioNormalizationSettings: ad.AudioNormalizationSettings ? {
          Algorithm: ad.AudioNormalizationSettings.Algorithm || 'ITU_1770_1',
          AlgorithmControl: ad.AudioNormalizationSettings.AlgorithmControl || 'CORRECT_AUDIO',
          TargetLkfs: ad.AudioNormalizationSettings.TargetLkfs ?? undefined
        } : undefined,
        codecType: type,
        activeCodecTab: ad.activeCodecTab || 'basic',
        isMinimized: ad.isMinimized ?? false,
        CodecSettings: ad.CodecSettings || {}
      };

      if (!res.CodecSettings[type]) {
        res.CodecSettings[type] = this.createDefaultAudioCodecSettings(type);
      }
      return res;
    },
    createDefaultAudioCodecSettings(type) {
      const defaults = {
        AacSettings: {
          Bitrate: 192000,
          CodingMode: 'CODING_MODE_2_0',
          InputType: 'NORMAL',
          Profile: 'LC',
          RateControlMode: 'CBR',
          RawFormat: 'NONE',
          SampleRate: 48000,
          Spec: 'MPEG4',
          VbrQuality: undefined
        },
        Ac3Settings: {
          Bitrate: 192000,
          BitstreamMode: 'COMPLETE_MAIN',
          CodingMode: 'CODING_MODE_2_0',
          Dialnorm: undefined,
          DrcProfile: 'FILM_STANDARD',
          AttenuationControl: 'NONE',
          LfeFilter: 'DISABLED',
          MetadataControl: 'USE_CONFIGURED'
        },
        Eac3Settings: {
          AttenuationControl: 'NONE',
          Bitrate: 256000,
          BitstreamMode: 'COMPLETE_MAIN',
          CodingMode: 'CODING_MODE_3_2',
          DcFilter: 'ENABLED',
          Dialnorm: undefined,
          DrcLine: 'FILM_STANDARD',
          DrcRf: 'FILM_STANDARD',
          LfeControl: 'LFE',
          LfeFilter: 'ENABLED',
          LoRoCenterMixLevel: -3,
          LoRoSurroundMixLevel: -3,
          LtRtCenterMixLevel: -3,
          LtRtSurroundMixLevel: -3,
          MetadataControl: 'USE_CONFIGURED',
          PassthroughControl: 'NO_PASSTHROUGH',
          PhaseControl: 'SHIFT_90_DEGREES',
          StereoDownmix: 'NOT_INDICATED',
          SurroundExMode: 'DISABLED',
          SurroundMode: 'NOT_INDICATED'
        },
        Eac3AtmosSettings: {
          Bitrate: 448000,
          CodingMode: 'CODING_MODE_5_1_4',
          Dialnorm: undefined,
          DrcLine: 'FILM_STANDARD',
          DrcRf: 'FILM_STANDARD',
          HeightTrim: -3,
          SurroundTrim: -3
        },
        Mp2Settings: {
          Bitrate: 192000,
          CodingMode: 'CODING_MODE_2_0',
          SampleRate: 48000
        },
        WavSettings: {
          BitDepth: 16,
          CodingMode: 'CODING_MODE_2_0',
          SampleRate: 48000
        },
        PassThroughSettings: {}
      };
      return JSON.parse(JSON.stringify(defaults[type] || {}));
    },
    onCodecTypeChange(video) {
        this.$set(video, 'CodecSettings', {});
        if (video.codecType === 'H265Settings') {
          const h265 = {};
          if (!h265.RateControlMode) h265.RateControlMode = 'CBR';
          if (!h265.ScanType) h265.ScanType = 'PROGRESSIVE';
          if (!h265.GopSizeUnits) h265.GopSizeUnits = 'FRAMES';
          if (!h265.GopSize) h265.GopSize = 90;
          if (!h265.GopClosedCadence) h265.GopClosedCadence = 1;
          if (!h265.SceneChangeDetect) h265.SceneChangeDetect = 'ENABLED';
          if (!h265.AfdSignaling) h265.AfdSignaling = 'NONE';
          if (!h265.TimecodeInsertion) h265.TimecodeInsertion = 'DISABLED';
          if (!h265.Profile) h265.Profile = 'MAIN';
          if (!h265.Tier) h265.Tier = 'MAIN';
          if (!h265.Level) h265.Level = 'H265_LEVEL_AUTO';
          if (!h265.Deblocking) h265.Deblocking = 'ENABLED';
          if (!h265.TilePadding) h265.TilePadding = 'NONE';
          if (!h265.MvTemporalPredictor) h265.MvTemporalPredictor = 'ENABLED';
          if (!h265.MvOverPictureBoundaries) h265.MvOverPictureBoundaries = 'ENABLED';
          if (!h265.TreeblockSize) h265.TreeblockSize = 'AUTO';
          if (!h265.LookAheadRateControl) h265.LookAheadRateControl = 'HIGH';
          if (!h265.AdaptiveQuantization) h265.AdaptiveQuantization = 'HIGH';
          if (!h265.FlickerAq) h265.FlickerAq = 'ENABLED';
          if (!h265.ColorMetadata) h265.ColorMetadata = 'INSERT';
          if (!h265.AlternativeTransferFunction) h265.AlternativeTransferFunction = 'OMIT';
          if (h265.FramerateNumerator === undefined) h265.FramerateNumerator = undefined;
          if (h265.FramerateDenominator === undefined) h265.FramerateDenominator = undefined;
          if (h265.ParNumerator === undefined) h265.ParNumerator = 1;
          if (h265.ParDenominator === undefined) h265.ParDenominator = 1;
          if (h265.SubgopLength === undefined) h265.SubgopLength = 'FIXED';
          if (h265.FilterSettings === undefined) h265.FilterSettings = undefined;
          this.$set(video.CodecSettings, 'H265Settings', h265);
        } else if (video.codecType === 'H264Settings') {
          const h264 = {};
          this.$set(h264, 'ParControl', 'INITIALIZE_FROM_SOURCE');
          this.$set(h264, 'RateControlMode', 'CBR');
          this.$set(h264, 'Bitrate', 5000000);
          this.$set(h264, 'ScanType', 'PROGRESSIVE');
          this.$set(h264, 'GopSizeUnits', 'FRAMES');
          this.$set(h264, 'GopSize', 90);
          this.$set(h264, 'GopClosedCadence', 1);
          this.$set(h264, 'SceneChangeDetect', 'ENABLED');
          this.$set(h264, 'AfdSignaling', 'NONE');
          this.$set(h264, 'TimecodeInsertion', 'DISABLED');
          this.$set(h264, 'Profile', 'MAIN');
          this.$set(h264, 'Level', 'H264_LEVEL_AUTO');
          this.$set(h264, 'EntropyEncoding', 'CABAC');
          this.$set(h264, 'Slices', undefined);
          this.$set(h264, 'AdaptiveQuantization', 'AUTO');
          this.$set(h264, 'SpatialAq', 'ENABLED');
          this.$set(h264, 'TemporalAq', 'ENABLED');
          this.$set(h264, 'Syntax', 'DEFAULT');
          this.$set(h264, 'FlickerAq', 'ENABLED');
          this.$set(h264, 'Softness', undefined);
          this.$set(h264, 'ColorMetadata', 'INSERT');
          this.$set(h264, 'LookAheadRateControl', 'MEDIUM');
          this.$set(h264, 'FramerateNumerator', undefined);
          this.$set(h264, 'FramerateDenominator', undefined);
          this.$set(h264, 'ParNumerator', undefined);
          this.$set(h264, 'ParDenominator', undefined);
          this.$set(h264, 'FramerateControl', 'INITIALIZE_FROM_SOURCE');
          this.$set(h264, 'SubgopLength', 'FIXED');
          this.$set(h264, 'GopBReference', 'DISABLED');
          this.$set(h264, 'FilterSettings', undefined);
          this.$set(h264, 'ForceFieldPictures', 'DISABLED');
          this.$set(h264, 'NumRefFrames', 1);

          this.$set(video.CodecSettings, 'H264Settings', h264);
        } else if (video.codecType === 'FrameCaptureSettings') {
            const fc = {};
            if (fc.CaptureInterval === undefined) fc.CaptureInterval = 10;
            if (!fc.CaptureIntervalUnits) fc.CaptureIntervalUnits = 'SECONDS';
            this.$set(video.CodecSettings, 'FrameCaptureSettings', fc);
        } else {
            video.CodecSettings = {};
        }

        // Initialize FilterSettings for H264/H265 if they don't exist
        if (['H264Settings', 'H265Settings'].includes(video.codecType)) {
          const codec = video.CodecSettings[video.codecType];
          if (!codec.FilterSettings) {
            this.$set(codec, 'FilterSettings', undefined);
          }
        }

        this.updateJson();
    },
    createDefaultOutput() {
      return {
        OutputName: "",
        VideoDescriptionName: "",
        OutputSettings: {
          ArchiveOutputSettings: {
            ContainerSettings: { M2tsSettings: {} },
            Extension: "",
            NameModifier: ""
          },
          RtmpOutputSettings: {
            CertificateMode: "SELF_SIGNED",
            ConnectionRetryInterval: 2,
            Destination: { DestinationRefId: "FROM_TEMPLATE" },
            NumRetries: 10,
          },
          HlsOutputSettings: {
            HlsSettingsType: "StandardHlsSettings",
            H265PackagingType: "HVC1",
            NameModifier: "",
            SegmentModifier: "",
            HlsSettings: {
              HlsSettingsChoice: "STANDARD_HLS",
              StandardHlsSettings: {
                AudioRenditionSets: "program_audio",
                M3u8Settings: {
                  AudioFramesPerPes: 2,
                  AudioPids: "492-498",
                  NielsenId3Behavior: "NO_PASSTHROUGH",
                  PatInterval: undefined,
                  PcrControl: "PCR_EVERY_PES_PACKET",
                  PcrPeriod: undefined,
                  PcrPid: undefined,
                  PmtInterval: undefined,
                  PmtPid: "480",
                  ProgramNum: 1,
                  Scte35Behavior: "NO_PASSTHROUGH",
                  Scte35Pid: "500",
                  TimedMetadataBehavior: "NO_PASSTHROUGH",
                  TimedMetadataPid: "502",
                  TransportStreamId: 1,
                  VideoPid: "481",
                  KlvBehavior: "NO_PASSTHROUGH",
                  KlvDataPids: undefined,
                },
              },
            },
          },
        },
      };
    },
    addDestination() {
      if (this.config.destinations.length >= 1) return;
      const newId = `Destination-${this.config.destinations.length + 1}`;
      this.config.destinations.push({
        Id: newId,
        Settings: [{ Url: "", StreamName: "" }, { Url: "", StreamName: "" }]
      });
      this.replaceDestinationRefs('FROM_TEMPLATE', newId);
      this.updateJson();
    },
    replaceDestinationRefs(oldId, newId) {
      if (!oldId || !newId || oldId === newId) return;
      const hls = this.config.outputGroup?.outputGroupSettings?.HlsGroupSettings;
      if (hls?.Destination?.DestinationRefId === oldId) hls.Destination.DestinationRefId = newId;
      const archive = this.config.outputGroup?.outputGroupSettings?.ArchiveGroupSettings;
      if (archive?.Destination?.DestinationRefId === oldId) archive.Destination.DestinationRefId = newId;
      if (this.config.outputs) {
        this.config.outputs.forEach(output => {
          if (output.OutputSettings?.RtmpOutputSettings?.Destination?.DestinationRefId === oldId) {
            output.OutputSettings.RtmpOutputSettings.Destination.DestinationRefId = newId;
          }
        });
      }
    },
    onDestinationIdFocus(id) {
      this._destOldId = id;
    },
    onDestinationIdInput(dest, event) {
      const newId = event.target.value;
      if (this._destOldId !== undefined && this._destOldId !== newId) {
        this.replaceDestinationRefs(this._destOldId, newId);
        this._destOldId = newId;
      }
      this.updateJson();
    },
    removeDestination(index) {
      this.config.destinations.splice(index, 1);
      this.updateJson();
    },
    addOutput() {
      this.config.outputGroup.outputs.push(this.createDefaultOutput());
      this.updateJson();
    },
    removeOutput(index) {
      if (this.config.outputGroup.outputs.length > 1) {
        this.config.outputGroup.outputs.splice(index, 1);
        this.updateJson();
      }
    },
    onRtmpAdMarkersChange(event) {
      const markers = this.config.outputGroup.outputGroupSettings.RtmpGroupSettings.AdMarkers || [];
      if (event.target.checked) {
        if (!markers.includes('ON_CUE_POINT_SCTE35')) {
          this.$set(this.config.outputGroup.outputGroupSettings.RtmpGroupSettings, 'AdMarkers', [...markers, 'ON_CUE_POINT_SCTE35']);
        }
      } else {
        this.$set(this.config.outputGroup.outputGroupSettings.RtmpGroupSettings, 'AdMarkers', markers.filter(m => m !== 'ON_CUE_POINT_SCTE35'));
      }
      this.updateJson();
    },
    onHlsSettingsTypeChange(output) {
      const type = output.OutputSettings.HlsOutputSettings.HlsSettingsType;
      const hlsSettings = output.OutputSettings.HlsOutputSettings.HlsSettings;

      // Clear existing choices
      delete hlsSettings.StandardHlsSettings;
      delete hlsSettings.Fmp4HlsSettings;
      delete hlsSettings.AudioOnlyHlsSettings;
      delete hlsSettings.FrameCaptureHlsSettings;

      if (type === 'StandardHlsSettings') {
        hlsSettings.HlsSettingsChoice = 'STANDARD_HLS';
        hlsSettings.StandardHlsSettings = {
          AudioRenditionSets: "program_audio",
          M3u8Settings: {
            AudioFramesPerPes: 2,
            AudioPids: "492-498",
            NielsenId3Behavior: "NO_PASSTHROUGH",
            PatInterval: 100,
            PcrControl: "PCR_EVERY_PES_PACKET",
            PcrPeriod: 20,
            PcrPid: "",
            PmtInterval: 100,
            PmtPid: "480",
            ProgramNum: 1,
            Scte35Behavior: "NO_PASSTHROUGH",
            Scte35Pid: "500",
            TimedMetadataBehavior: "NO_PASSTHROUGH",
            TimedMetadataPid: "502",
            TransportStreamId: 1,
            VideoPid: "481",
            KlvBehavior: "NO_PASSTHROUGH",
            KlvDataPids: "",
          }
        };
      } else if (type === 'Fmp4HlsSettings') {
        hlsSettings.HlsSettingsChoice = 'FMP4_HLS';
        hlsSettings.Fmp4HlsSettings = {
          AudioRenditionSets: "program_audio",
          NielsenId3Behavior: "NO_PASSTHROUGH",
          TimedMetadataBehavior: "NO_PASSTHROUGH"
        };
      } else if (type === 'AudioOnlyHlsSettings') {
        hlsSettings.HlsSettingsChoice = 'AUDIO_ONLY_HLS';
        hlsSettings.AudioOnlyHlsSettings = {
          AudioGroupId: "program_audio",
          AudioTrackType: "ALTERNATE_AUDIO_AUTO_SELECT",
          SegmentType: "AAC",
          AudioOnlyImage: {
            Uri: "",
            PasswordParam: "",
            Username: ""
          }
        };
      } else if (type === 'FrameCaptureHlsSettings') {
        hlsSettings.HlsSettingsChoice = 'FRAME_CAPTURE_HLS';
        hlsSettings.FrameCaptureHlsSettings = {};
      }
      this.updateJson();
    },
    getHlsSettings(output) {
      const type = output.OutputSettings.HlsOutputSettings.HlsSettingsType;
      return output.OutputSettings.HlsOutputSettings.HlsSettings[type];
    },
    updateJson() {
      const type = this.config.outputGroup.type;

      // Only emit the relevant group settings key for the selected type
      let cleanGroupSettings = {};
      if (type === 'HLS') {
        cleanGroupSettings = { HlsGroupSettings: this.config.outputGroup.outputGroupSettings.HlsGroupSettings };
      } else if (type === 'ARCHIVE') {
        cleanGroupSettings = { ArchiveGroupSettings: this.config.outputGroup.outputGroupSettings.ArchiveGroupSettings };
      } else if (type === 'RTMP') {
        cleanGroupSettings = { RtmpGroupSettings: this.config.outputGroup.outputGroupSettings.RtmpGroupSettings };
      }

      // Only emit the relevant output settings key for each output
      const cleanOutputs = this.config.outputGroup.outputs.map(output => {
        let cleanOutputSettings = {};
        if (type === 'HLS') {
          cleanOutputSettings = { HlsOutputSettings: output.OutputSettings.HlsOutputSettings };
        } else if (type === 'ARCHIVE') {
          cleanOutputSettings = { ArchiveOutputSettings: output.OutputSettings.ArchiveOutputSettings };
        } else if (type === 'RTMP') {
          cleanOutputSettings = { RtmpOutputSettings: output.OutputSettings.RtmpOutputSettings };
        }
        return { ...output, OutputSettings: cleanOutputSettings };
      });

      // Strip StreamName from destinations for non-RTMP types
      const cleanDestinations = this.config.destinations.map(dest => {
        const settings = dest.Settings.map(s => {
          if (type === 'RTMP') {
            const entry = { Url: s.Url };
            if (s.StreamName) entry.StreamName = s.StreamName;
            return entry;
          }
          return { Url: s.Url };
        });
        return { ...dest, Settings: settings };
      });

      const payload = {
        ...this.config,
        outputGroup: {
          ...this.config.outputGroup,
          outputGroupSettings: cleanGroupSettings,
          outputs: cleanOutputs,
        },
      };

      if (cleanDestinations.length > 0) {
        payload.destinations = cleanDestinations;
      } else {
        delete payload.destinations;
      }

      const isValid = this.validateForm();
      this.$emit("input", JSON.stringify(payload));
      this.$emit("validation-change", { isValid, errors: this.validationErrors });
    },
    validateForm() {
      this.validationErrors = [];
      const err = (msg) => this.validationErrors.push(msg);

      // 1. Basic Structure
      if (!this.config.outputGroup.name) err("Output Group Name is required.");
      if (this.config.outputGroup.type === 'HLS' && !this.config.outputGroup?.outputGroupSettings?.HlsGroupSettings?.Destination?.DestinationRefId) err("HLS Destination is required.");
      if (this.config.outputGroup.type === 'ARCHIVE' && !this.config.outputGroup?.outputGroupSettings?.ArchiveGroupSettings?.Destination?.DestinationRefId) err("Archive Destination is required.");

      // 2. Outputs Validation
      if (!this.config.outputGroup.outputs || this.config.outputGroup.outputs.length === 0) {
        err("At least one Output is required.");
      } else {
        this.config.outputGroup.outputs.forEach((output, idx) => {
          const name = output.OutputName || `Output #${idx + 1}`;
          if (!output.OutputName || output.OutputName === 'None') err(`${name}: Output Name is required.`);
          if (!output.VideoDescriptionName || output.VideoDescriptionName === 'None') err(`${name}: Video Description mapping is required.`);
          if (!output.AudioDescriptionNames || output.AudioDescriptionNames.length === 0 || (output.AudioDescriptionNames.length === 1 && output.AudioDescriptionNames[0] === 'None')) {
            err(`${name}: At least one Audio Description mapping is required.`);
          }

          if (this.config.outputGroup.type === 'HLS') {
            if (!output.OutputSettings?.HlsOutputSettings?.NameModifier || output.OutputSettings?.HlsOutputSettings?.NameModifier === 'None') err(`${name}: Name Modifier is required.`);
          } else if (this.config.outputGroup.type === 'ARCHIVE') {
            if (!output.OutputSettings?.ArchiveOutputSettings?.NameModifier || output.OutputSettings?.ArchiveOutputSettings?.NameModifier === 'None') err(`${name}: Name Modifier is required.`);
          } else if (this.config.outputGroup.type === 'RTMP') {
            if (!output.OutputSettings?.RtmpOutputSettings?.Destination?.DestinationRefId) err(`${name}: RTMP Destination is required.`);
          }
        });
      }

      // 3. Video Descriptions Validation
      const framerateRequiredCodecs = ['H265Settings', 'Mpeg2Settings', 'Av1Settings'];
      if (!this.config.videoDescriptions || this.config.videoDescriptions.length === 0) {
        err("At least one Video Description is required.");
      } else {
        this.config.videoDescriptions.forEach((video, idx) => {
          const name = video.Name || `Video Description #${idx + 1}`;
          if (!video.Name || video.Name === 'None') err(`${name}: Name is required.`);
          if (!video.codecType || video.codecType === 'None' || video.codecType === 'NONE') err(`${name}: Codec Type is required.`);
          if (framerateRequiredCodecs.includes(video.codecType)) {
            const cs = video.CodecSettings?.[video.codecType];
            if (!cs?.FramerateNumerator) err(`${name}: Framerate Numerator is required for ${video.codecType}.`);
            if (!cs?.FramerateDenominator) err(`${name}: Framerate Denominator is required for ${video.codecType}.`);
          }
        });
      }

      // 4. Audio Descriptions Validation
      if (this.config.audioDescriptions && this.config.audioDescriptions.length > 0) {
        this.config.audioDescriptions.forEach((audio, idx) => {
          const name = audio.Name || `Audio Description #${idx + 1}`;
          if (!audio.Name || audio.Name === 'None') err(`${name}: Name is required.`);
          if (!audio.AudioSelectorName || audio.AudioSelectorName === 'None') err(`${name}: Audio Selector Name is required.`);
          if (!audio.codecType || audio.codecType === 'None' || audio.codecType === 'NONE') err(`${name}: Codec Type is required.`);
        });
      }

      // 5. Destinations Validation
      if (this.config.destinations && this.config.destinations.length > 0) {
        this.config.destinations.forEach((dest, idx) => {
          if (!dest.Id) err(`Destination #${idx + 1}: ID is required.`);
          if (!dest.Settings || !dest.Settings[0]?.Url) err(`Destination #${idx + 1}: URL 1 is required.`);
        });
      }

      return this.validationErrors.length === 0;
    },
  },
};
</script>
